// ADR-012 i18n — "knowledge" cluster label catalog. Spread into the central LABELS
// in labels.ts. EN values MUST equal the screen's current hardcoded English so
// existing text-based tests stay green; HE values are the Hebrew translation.
//
// Covers: Knowledge Banks (list / form / detail / Sources / Chunks / Discovery),
// Learning, Bot Entities (list / form), Channels (list / form).
export const KNOWLEDGE_LABELS = {
  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge Banks — list
  // ═══════════════════════════════════════════════════════════════════════
  kbListLoading: { en: 'Loading knowledge banks...', he: 'טוען מאגרי ידע...' },
  kbListTitle: { en: 'Knowledge Banks', he: 'מאגרי ידע' },
  kbListCreate: { en: '+ Create bank', he: '+ צור מאגר' },
  kbListLoadError: { en: 'Failed to load knowledge banks.', he: 'טעינת מאגרי הידע נכשלה.' },
  kbListDeleteBlocked: {
    en: 'Cannot delete "{name}" — {count} agent(s) still link to it. Unlink them first by editing each agent\'s Connected Knowledge Banks.',
    he: 'לא ניתן למחוק את "{name}" — {count} סוכנים עדיין מקושרים אליו. יש לבטל תחילה את הקישור על ידי עריכת מאגרי הידע המקושרים של כל סוכן.',
  },
  kbListDeleteConfirm: {
    en: 'Delete bank "{name}" and all its sources/chunks?',
    he: 'למחוק את המאגר "{name}" ואת כל המקורות/פריטי הידע שלו?',
  },
  kbListDeleteLinked: { en: 'One or more agents still link to this bank.', he: 'סוכן אחד או יותר עדיין מקושרים למאגר זה.' },
  kbListDeleteFailed: { en: 'Delete failed.', he: 'המחיקה נכשלה.' },
  kbListEmpty: { en: 'No banks yet. Create one to start curating knowledge.', he: 'אין עדיין מאגרים. צור אחד כדי להתחיל לנהל ידע.' },
  kbListColName: { en: 'Name', he: 'שם' },
  kbListColPurpose: { en: 'Purpose', he: 'מטרה' },
  kbListColSources: { en: 'Sources', he: 'מקורות' },
  kbListColKnowledgeEntries: { en: 'Knowledge entries', he: 'פריטי ידע' },
  kbListColLinkedAgents: { en: 'Linked agents', he: 'סוכנים מקושרים' },
  kbListColUpdated: { en: 'Updated', he: 'עודכן' },
  kbListColActions: { en: 'Actions', he: 'פעולות' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge Banks — create / edit form
  // ═══════════════════════════════════════════════════════════════════════
  kbFormLoading: { en: 'Loading bank...', he: 'טוען מאגר...' },
  kbFormLoadError: { en: 'Failed to load bank.', he: 'טעינת המאגר נכשלה.' },
  kbFormEditTitle: { en: 'Edit Knowledge Bank', he: 'עריכת מאגר ידע' },
  kbFormCreateTitle: { en: 'Create Knowledge Bank', he: 'יצירת מאגר ידע' },
  kbFormNameLabel: { en: 'Name', he: 'שם' },
  kbFormNamePlaceholder: {
    en: 'Technical Support, Sales FAQ, Product Docs…',
    he: 'תמיכה טכנית, שאלות נפוצות למכירות, מסמכי מוצר…',
  },
  kbFormPurposeLabel: { en: 'Purpose', he: 'מטרה' },
  kbFormPurposeHelp: {
    en: 'A casual one-liner is fine. The Refine button (after save) will auto-shape this into a structured spec our AI uses for grading.',
    he: 'משפט קצר ולא רשמי מספיק. כפתור העידון (לאחר השמירה) יעצב זאת אוטומטית למפרט מובנה שה-AI שלנו משתמש בו לדירוג.',
  },
  kbFormPurposePlaceholder: {
    en: 'Help customers with product questions, password resets, billing issues…',
    he: 'עזרה ללקוחות בשאלות על המוצר, איפוס סיסמאות, בעיות חיוב…',
  },
  kbFormRefine: { en: '✨ Refine', he: '✨ עדן' },
  kbFormRefining: { en: 'Refining…', he: 'מעדן…' },
  kbFormRefineTypePurpose: { en: 'Type a purpose first.', he: 'הקלד תחילה מטרה.' },
  kbFormRefineSaveFirst: {
    en: 'Save the bank first, then click Refine to auto-shape the purpose.',
    he: 'שמור תחילה את המאגר, ואז לחץ על עדן כדי לעצב את המטרה אוטומטית.',
  },
  kbFormRefinedWithConditions: {
    en: 'Refined purpose updated with {count} condition{s}. Edit if you want, then Save.',
    he: 'המטרה המעודנת עודכנה עם {count} תנאים. ניתן לערוך ואז לשמור.',
  },
  kbFormRefinedNoConditions: {
    en: 'Refined purpose updated. Edit if you want, then Save.',
    he: 'המטרה המעודנת עודכנה. ניתן לערוך ואז לשמור.',
  },
  kbFormRefineClarify: {
    en: 'Our AI needs more detail: "{q}"',
    he: 'ה-AI שלנו זקוק לפרטים נוספים: "{q}"',
  },
  kbFormRefineFailed: {
    en: 'Refine failed; you can write the refined purpose yourself.',
    he: 'העידון נכשל; ניתן לכתוב את המטרה המעודנת בעצמך.',
  },
  kbFormSaveFailed: { en: 'Save failed.', he: 'השמירה נכשלה.' },
  kbFormRefinedLabel: { en: 'Refined purpose (used by grading)', he: 'מטרה מעודנת (משמשת לדירוג)' },
  kbFormRefinedHelp: { en: 'Auto-populated by Refine. Editable.', he: 'מתמלא אוטומטית על ידי עידון. ניתן לעריכה.' },
  kbFormRefinedPlaceholderEdit: {
    en: '(empty — click Refine above to auto-fill)',
    he: '(ריק — לחץ על עדן למעלה למילוי אוטומטי)',
  },
  kbFormRefinedPlaceholderNew: { en: '(will be auto-filled on save)', he: '(יתמלא אוטומטית בשמירה)' },
  kbFormConditions: { en: 'Conditions ({count})', he: 'תנאים ({count})' },
  kbFormConditionsHint: { en: '— structured view of the bullets above', he: '— תצוגה מובנית של הנקודות שלמעלה' },
  kbFormGradeLabel: { en: 'Grade criteria (optional)', he: 'קריטריוני דירוג (אופציונלי)' },
  kbFormGradeHelp: {
    en: 'Soft rules concatenated under the refined purpose in the grading prompt.',
    he: 'כללים רכים המשורשרים תחת המטרה המעודנת בהנחיית הדירוג.',
  },
  kbFormGradePlaceholder: {
    en: 'Prefer step-by-step how-tos. Deprioritise marketing pages. Skip blog posts older than 2024.',
    he: 'העדף מדריכים שלב-אחר-שלב. הורד בעדיפות דפי שיווק. דלג על פוסטים מהבלוג שקדמו ל-2024.',
  },
  kbFormShareableLabel: { en: 'Customer visibility', he: 'נראות ללקוחות' },
  kbFormShareableHelp: {
    en: 'Off by default. While off the bot can still READ these images to answer questions — it just cannot send one to a customer. Crawled pages carry third-party images nobody here authored, so review a bank before opening it.',
    he: 'כבוי כברירת מחדל. כשהאפשרות כבויה הבוט עדיין יכול לקרוא את התמונות כדי לענות — הוא פשוט לא יכול לשלוח אותן ללקוח. דפים שנסרקו מכילים תמונות של צד שלישי שאיש כאן לא יצר, לכן כדאי לעבור על המאגר לפני פתיחתו.',
  },
  kbFormShareableCheckbox: {
    en: 'The bot may show this bank’s images to customers',
    he: 'הבוט רשאי להציג ללקוחות תמונות ממאגר זה',
  },
  kbFormStrictnessLabel: { en: 'Match strictness', he: 'מידת התאמה נדרשת' },
  kbFormStrictnessHelp: {
    en: 'How closely an article must match the customer’s words to be found. The platform default is 0.4. Lower finds more articles (useful when customers write in another language than the articles) but with looser matches; higher is stricter. The bot still decides what it actually uses.',
    he: 'עד כמה מאמר צריך להתאים לניסוח של הלקוח כדי להימצא. ברירת המחדל של הפלטפורמה היא 0.4. ערך נמוך מוצא יותר מאמרים (שימושי כשהלקוחות כותבים בשפה אחרת מזו של המאמרים) אך בהתאמה רופפת יותר; ערך גבוה מחמיר יותר. הבוט עדיין מחליט במה להשתמש בפועל.',
  },
  kbFormStrictnessDefault: { en: 'Use platform default (0.4)', he: 'השתמש בברירת המחדל של הפלטפורמה (0.4)' },
  kbFormStrictnessRange: { en: 'Enter a value between 0.1 and 0.9.', he: 'יש להזין ערך בין 0.1 ל-0.9.' },
  kbFormCreateSubmit: { en: 'Create bank', he: 'צור מאגר' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge Banks — detail page
  // ═══════════════════════════════════════════════════════════════════════
  kbDetailLoading: { en: 'Loading bank...', he: 'טוען מאגר...' },
  kbDetailNotFound: { en: 'Bank not found.', he: 'המאגר לא נמצא.' },
  kbDetailBackToBanks: { en: '← Back to banks', he: '← חזרה למאגרים' },
  kbDetailNoPurpose: { en: '(no purpose set)', he: '(לא הוגדרה מטרה)' },
  kbDetailStatSources: { en: 'Sources:', he: 'מקורות:' },
  kbDetailStatEntries: { en: 'Knowledge entries:', he: 'פריטי ידע:' },
  kbDetailStatLinkedAgents: { en: 'Linked agents:', he: 'סוכנים מקושרים:' },
  kbDetailStatUpdated: { en: 'Updated:', he: 'עודכן:' },
  kbDetailTabDiscovery: { en: 'Discovery', he: 'גילוי' },
  kbDetailTabSources: { en: 'Sources', he: 'מקורות' },
  kbDetailTabEntries: { en: 'Knowledge entries ({count})', he: 'פריטי ידע ({count})' },
  kbDetailTabMedia: { en: 'Media', he: 'מדיה' },
  kbDetailOperationInProgress: { en: 'Operation in progress…', he: 'פעולה מתבצעת…' },
  kbDetailReparseButton: { en: 'Re-read pages', he: 'קרא דפים מחדש' },
  // Keeps the button's own verb while it runs. `kbDetailOperationInProgress` is still used by the
  // progress banner, which is the right place for a generic status — a BUTTON has to stay
  // identifiable, and two buttons wearing the same label are just two grey rectangles.
  kbDetailReparseButtonRunning: { en: 'Re-reading pages…', he: 'קורא דפים מחדש…' },
  kbDetailReparseConfirm: {
    en: 'Re-read every URL source under this bank? Existing entries for those sources will be replaced. File sources are skipped.',
    he: 'לקרוא מחדש כל מקור כתובת URL במאגר זה? פריטים קיימים עבור מקורות אלה יוחלפו. מקורות קבצים ידולגו.',
  },
  kbDetailReparseFailed: { en: 'Re-parse failed to start.', he: 'הפעלת הקריאה מחדש נכשלה.' },
  kbDetailReembedButton: { en: 'Refresh search index', he: 'רענן את אינדקס החיפוש' },
  kbDetailReembedButtonRunning: { en: 'Refreshing search index…', he: 'מרענן את אינדקס החיפוש…' },
  kbDetailReembedConfirm: {
    en: 'Rebuild the search index for every entry in this bank using the current AI settings? Use this after changing those settings — the old search data becomes unusable.',
    he: 'לבנות מחדש את אינדקס החיפוש עבור כל פריט במאגר זה לפי הגדרות ה-AI הנוכחיות? השתמש בכך לאחר שינוי ההגדרות — נתוני חיפוש ישנים הופכים לבלתי שמישים.',
  },
  kbDetailReembedFailed: { en: 'Re-embed failed to start.', he: 'הפעלת ההטמעה מחדש נכשלה.' },
  kbDetailOperationFailedGeneric: { en: 'This operation failed. Try again, or contact support if it keeps happening.', he: 'הפעולה נכשלה. נסה שוב, או פנה לתמיכה אם זה חוזר על עצמו.' },
  kbDetailVerbReparsed: { en: 'Re-parsed', he: 'נקרא מחדש' },
  kbDetailVerbReparsing: { en: 'Re-parsing', he: 'קורא מחדש' },
  kbDetailVerbReembedded: { en: 'Re-embedded', he: 'הוטמע מחדש' },
  kbDetailVerbReembedding: { en: 'Re-embedding', he: 'מטמיע מחדש' },
  kbDetailVerbIndexed: { en: 'Indexed', he: 'התווסף לאינדקס' },
  kbDetailVerbIndexing: { en: 'Indexing', he: 'מוסיף לאינדקס' },
  kbDetailLastItem: { en: ' — last: ', he: ' — אחרון: ' },
  kbDetailStatusFailed: { en: '❌ failed', he: '❌ נכשל' },
  kbDetailStatusComplete: { en: '✓ complete', he: '✓ הושלם' },
  kbDetailStatusRunning: { en: '⏳ running', he: '⏳ פועל' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge Banks — Sources tab
  // ═══════════════════════════════════════════════════════════════════════
  sourcesTabUploading: { en: 'Uploading…', he: 'מעלה…' },
  sourcesTabDropZone: { en: 'Drop files here, or click to browse', he: 'גרור קבצים לכאן, או לחץ לעיון' },
  sourcesTabFileTypes: {
    en: 'PDF, DOCX, XLSX, PPTX, HTML, MD, TXT, CSV, JSON, or image (max 50 MB each)',
    he: 'PDF, DOCX, XLSX, PPTX, HTML, MD, TXT, CSV, JSON או תמונה (עד 50 MB לכל קובץ)',
  },
  sourcesTabLoadError: { en: 'Failed to load sources.', he: 'טעינת המקורות נכשלה.' },
  sourcesTabUploadFailed: {
    en: '{failed}/{total} upload(s) failed — see console.',
    he: '{failed}/{total} העלאות נכשלו — ראה קונסולה.',
  },
  sourcesTabToggleFailed: { en: 'Toggle failed.', he: 'ההחלפה נכשלה.' },
  sourcesTabRemoveConfirm: {
    en: 'Remove "{name}" and every chunk it produced? This cannot be undone.',
    he: 'להסיר את "{name}" ואת כל המקטעים שהוא יצר? לא ניתן לבטל פעולה זו.',
  },
  sourcesTabRemoveFailed: { en: 'Remove failed.', he: 'ההסרה נכשלה.' },
  sourcesTabLoading: { en: 'Loading sources…', he: 'טוען מקורות…' },
  sourcesTabEmpty: {
    en: 'No sources yet. Drop a file above, or use the Discovery tab to crawl URLs.',
    he: 'אין עדיין מקורות. גרור קובץ למעלה, או השתמש בלשונית גילוי כדי לסרוק כתובות URL.',
  },
  sourcesTabColKind: { en: 'Kind', he: 'סוג' },
  sourcesTabColName: { en: 'Name', he: 'שם' },
  sourcesTabColStatus: { en: 'Status', he: 'סטטוס' },
  // OP-166 T9.7: "Chunks" → "Knowledge entries" — the one place in the Knowledge Bank UI that was missed.
  sourcesTabColChunks: { en: 'Knowledge entries', he: 'פריטי ידע' },
  sourcesTabColUpdated: { en: 'Updated', he: 'עודכן' },
  sourcesTabColActions: { en: 'Actions', he: 'פעולות' },
  sourcesTabDisable: { en: 'Disable', he: 'השבת' },
  sourcesTabEnable: { en: 'Enable', he: 'הפעל' },
  sourcesTabRemove: { en: 'Remove', he: 'הסר' },

  // Multi-select + the bulk actions that run on the current selection.
  //
  // Selection is GENERIC — every row can be ticked, including uploaded files.
  // Applicability belongs to the ACTION, not to the checkbox: Delete accepts any
  // source, Re-index accepts only URL ones and says so. Tying selectability to
  // one action's rule is what made a file row un-tickable and therefore
  // un-deletable in bulk.
  // Select-by-path (Sources + Discovery): ticks every URL under a shared path, e.g. /tag/. It only
  // ticks; the tab's own Delete button and confirmation still do the deleting.
  selectByPathLabel: { en: 'Select by path', he: 'בחירה לפי נתיב' },
  selectByPathPlaceholder: { en: '/tag/  or  https://site.com/tag/', he: '/tag/  או  https://site.com/tag/' },
  selectByPathApply: { en: 'Select matching ({count})', he: 'בחר תואמים ({count})' },
  selectByPathNone: { en: 'No URL matches.', he: 'אין כתובת תואמת.' },
  sourcesTabSelectAll: { en: 'Select all sources', he: 'בחר את כל המקורות' },
  sourcesTabSelectRow: { en: 'Select this source', he: 'בחר מקור זה' },
  sourcesTabReindexSelected: { en: 'Re-index selected ({count})', he: 'אנדקס מחדש את הנבחרים ({count})' },
  sourcesTabReindexRunning: { en: 'Re-indexing…', he: 'מאנדקס מחדש…' },
  sourcesTabSelectedCount: { en: '{count} selected', he: '{count} נבחרו' },
  sourcesTabClearSelection: { en: 'Clear', he: 'נקה' },
  sourcesTabReindexConfirm: {
    en: 'Re-index {count} source(s)? Each page is fetched again and its entries are rebuilt from scratch — the existing entries for those sources are replaced.',
    he: 'לאנדקס מחדש {count} מקורות? כל דף ייקרא שוב והרשומות שלו ייבנו מחדש — הרשומות הקיימות של אותם מקורות יוחלפו.',
  },
  sourcesTabReindexStarted: {
    en: 'Re-index started for {count} source(s). Progress is shown at the top of the page.',
    he: 'האינדוקס מחדש החל עבור {count} מקורות. ההתקדמות מוצגת בראש העמוד.',
  },
  sourcesTabReindexFailed: { en: 'Re-index failed to start.', he: 'הפעלת האינדוקס מחדש נכשלה.' },
  // Why a FILE row in the selection is skipped by Re-index. Shown in the toolbar
  // whenever the selection contains one, so the gap between "38 selected" and
  // "Re-index (23)" is explained on the spot rather than looking like a bug.
  sourcesTabReindexFileUnsupported: {
    en: 'Uploaded files cannot be re-indexed — the original file is not kept after its text is extracted. Remove it and upload it again.',
    he: 'לא ניתן לאנדקס מחדש קבצים שהועלו — הקובץ המקורי אינו נשמר לאחר חילוץ הטקסט. הסר אותו והעלה מחדש.',
  },
  sourcesTabReindexSkipsFiles: {
    en: '{count} uploaded file(s) in this selection will be skipped — files cannot be re-indexed.',
    he: '{count} קבצים שהועלו בבחירה זו יידלגו — לא ניתן לאנדקס מחדש קבצים.',
  },

  // Bulk delete. Destructive and irreversible, so the confirm names the full
  // blast radius — sources, their knowledge entries AND their images — and calls
  // out the asymmetry that matters: a URL can be crawled again, an uploaded file
  // cannot, because its bytes are discarded once the text is extracted.
  sourcesTabDeleteSelected: { en: 'Delete selected ({count})', he: 'מחק את הנבחרים ({count})' },
  sourcesTabDeleteConfirm: {
    en: 'Permanently delete {count} source(s), the {chunks} knowledge entries built from them, and all their images?\n\nThis cannot be undone. URL sources can be crawled again from the Discovery tab; uploaded files cannot — their bytes are not kept after extraction, so you would need the original files to restore them.',
    he: 'למחוק לצמיתות {count} מקורות, את {chunks} פריטי הידע שנבנו מהם, ואת כל התמונות שלהם?\n\nלא ניתן לבטל פעולה זו. מקורות URL ניתן לסרוק שוב מלשונית הגילוי; קבצים שהועלו — לא, מכיוון שהבייטים שלהם אינם נשמרים לאחר החילוץ, ותידרש לקבצים המקוריים כדי לשחזר אותם.',
  },
  sourcesTabDeleting: { en: 'Deleting {done} of {total}…', he: 'מוחק {done} מתוך {total}…' },
  sourcesTabDeleteDone: { en: 'Deleted {count} source(s).', he: 'נמחקו {count} מקורות.' },
  sourcesTabDeletePartial: {
    en: 'Deleted {ok} of {total}. {failed} could not be deleted — they are still listed below.',
    he: 'נמחקו {ok} מתוך {total}. {failed} לא נמחקו — הם עדיין מופיעים ברשימה למטה.',
  },

  sourcesTabWorkingTitle: {
    en: 'Your file is being processed — this may take 30–120 seconds for longer documents.',
    he: 'הקובץ שלך בעיבוד — זה עשוי להימשך 30–120 שניות עבור מסמכים ארוכים יותר.',
  },
  sourcesTabProcessingFailedTooltip: {
    en: 'This item failed to process — try removing and re-adding it, or contact support if this keeps happening.',
    he: 'פריט זה נכשל בעיבוד — נסה להסיר ולהוסיף אותו מחדש, או פנה לתמיכה אם זה חוזר על עצמו.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge Banks — Chunks (Knowledge entries) tab
  // ═══════════════════════════════════════════════════════════════════════
  chunksTabLoading: { en: 'Loading knowledge entries...', he: 'טוען פריטי ידע...' },
  chunksTabSaveFailed: { en: 'Save failed.', he: 'השמירה נכשלה.' },
  chunksTabDeleteConfirm: { en: 'Delete this chunk?', he: 'למחוק מקטע זה?' },
  chunksTabDeleteFailed: { en: 'Delete failed.', he: 'המחיקה נכשלה.' },
  chunksTabEmpty: {
    en: 'No entries yet. Use the Discovery tab to ingest URLs, or click "+ Add entry" to author one manually.',
    he: 'אין עדיין פריטים. השתמש בלשונית גילוי כדי לקלוט כתובות URL, או לחץ על "+ הוסף פריט" כדי ליצור אחד ידנית.',
  },
  chunksTabAddEntry: { en: '+ Add entry', he: '+ הוסף פריט' },
  // WP3: friendly badge replacing raw `[status]` bracket notation on each entry row.
  chunksTabStatusParsed: { en: 'Processing', he: 'בעיבוד' },
  chunksTabStatusIndexed: { en: 'Searchable', he: 'ניתן לחיפוש' },
  chunksTabStatusManual: { en: 'Manually added', he: 'נוסף ידנית' },
  chunksTabManual: { en: '(manual)', he: '(ידני)' },
  chunksTabNewManual: { en: '(new manual entry)', he: '(פריט ידני חדש)' },
  chunksTabFrom: { en: 'from', he: 'מתוך' },
  chunksTabAnswerLabel: { en: 'Answer text', he: 'טקסט התשובה' },
  chunksTabSearchTermsLabel: { en: 'Search terms (Enter to add)', he: 'מונחי חיפוש (Enter להוספה)' },
  chunksTabPhrasingsLabel: {
    en: 'Phrasings customers might use (one per line)',
    he: 'ניסוחים שלקוחות עשויים להשתמש בהם (אחד בכל שורה)',
  },
  chunksTabSearchPlaceholder: { en: 'Type a search term and press Enter', he: 'הקלד מונח חיפוש ולחץ Enter' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge Banks — Media tab (K-5 gallery)
  // ═══════════════════════════════════════════════════════════════════════
  mediaTabLoading: { en: 'Loading media…', he: 'טוען מדיה…' },
  mediaTabLoadSourcesFailed: { en: 'Failed to load sources.', he: 'טעינת המקורות נכשלה.' },
  mediaTabLoadFailed: { en: 'Failed to load media.', he: 'טעינת המדיה נכשלה.' },
  mediaTabSourceLabel: { en: 'Source', he: 'מקור' },
  mediaTabNoSources: {
    en: 'No sources yet. Add one in the Sources tab first.',
    he: 'אין עדיין מקורות. הוסף מקור בלשונית מקורות תחילה.',
  },
  mediaTabEmpty: { en: 'No media for this source yet.', he: 'אין עדיין מדיה עבור מקור זה.' },
  mediaTabAllSources: { en: 'All sources', he: 'כל המקורות' },
  // Distinct from mediaTabEmpty on purpose: "none on this source" and "none anywhere in the bank"
  // are different facts, and showing the per-source wording in all-sources mode was exactly the
  // ambiguity this view exists to remove.
  mediaTabEmptyBank: {
    en: 'No media in this bank yet.',
    he: 'אין עדיין מדיה במאגר הזה.',
  },
  mediaTabCountAcrossSources: {
    en: '{count} item(s) across all sources',
    he: '{count} פריטים מכל המקורות',
  },
  mediaTabSourceUnknown: {
    en: '(source deleted)',
    he: '(המקור נמחק)',
  },
  mediaTabDeleteConfirm: {
    en: 'Delete this media item? This cannot be undone.',
    he: 'למחוק את פריט המדיה הזה? לא ניתן לבטל פעולה זו.',
  },
  mediaTabDeleteFailed: { en: 'Delete failed.', he: 'המחיקה נכשלה.' },

  // Multi-select + bulk delete, mirroring SourcesTab. Deleting a picture is
  // narrower than deleting a source — the source, its text and its other images
  // all survive — so the confirm is correspondingly shorter. It still says
  // "permanently", because the bytes go with the row.
  mediaTabSelectRow: { en: 'Select this image', he: 'בחר תמונה זו' },
  mediaTabSelectAll: { en: 'Select all shown', he: 'בחר את כל המוצגים' },
  mediaTabSelectedCount: { en: '{count} selected', he: '{count} נבחרו' },
  mediaTabClearSelection: { en: 'Clear', he: 'נקה' },
  mediaTabDeleteSelected: { en: 'Delete selected ({count})', he: 'מחק את הנבחרים ({count})' },
  mediaTabBulkDeleteConfirm: {
    en: 'Permanently delete {count} image(s)? The stored bytes go with them. Their sources and text entries are not affected.',
    he: 'למחוק לצמיתות {count} תמונות? הבייטים השמורים יימחקו יחד איתן. המקורות ופריטי הטקסט שלהם לא יושפעו.',
  },
  mediaTabDeleting: { en: 'Deleting {done} of {total}…', he: 'מוחק {done} מתוך {total}…' },
  mediaTabBulkDeleteDone: { en: 'Deleted {count} image(s).', he: 'נמחקו {count} תמונות.' },
  mediaTabBulkDeletePartial: {
    en: 'Deleted {ok} of {total}. {failed} could not be deleted — they are still shown below.',
    he: 'נמחקו {ok} מתוך {total}. {failed} לא נמחקו — הן עדיין מוצגות למטה.',
  },
  mediaTabPrimaryFailed: { en: 'Could not set as primary.', he: 'לא ניתן היה לסמן כראשי.' },
  mediaTabCaptionFailed: { en: 'Caption save failed.', he: 'שמירת הכיתוב נכשלה.' },
  mediaTabSetPrimary: { en: 'Set as primary', he: 'סמן כראשי' },
  mediaTabPrimaryBadge: { en: 'Primary', he: 'ראשי' },
  mediaTabCaptionLabel: { en: 'Caption', he: 'כיתוב' },
  mediaTabCaptionPlaceholder: { en: 'Describe what this shows…', he: 'תאר מה מוצג כאן…' },
  mediaTabOriginalBadge: { en: 'Original file', he: 'קובץ מקורי' },
  mediaTabImageBadge: { en: 'Image', he: 'תמונה' },
  mediaTabThumbnailUnavailable: { en: '(preview unavailable)', he: '(תצוגה מקדימה אינה זמינה)' },
  mediaTabOriginalNotPreviewable: { en: 'Original file (not previewable)', he: 'קובץ מקורי (אין תצוגה מקדימה)' },
  mediaTabShareableLabel: { en: 'Customer delivery', he: 'מסירה ללקוח' },
  mediaTabShareableInherit: { en: 'Inherit from source/bank', he: 'ירושה ממקור/מאגר' },
  mediaTabShareableRelease: { en: 'Release to customers', he: 'שחרר ללקוחות' },
  mediaTabShareableHoldBack: { en: 'Hold back', he: 'החזק בחזרה' },
  mediaTabShareableFailed: { en: 'Could not update customer delivery.', he: 'לא ניתן היה לעדכן מסירה ללקוח.' },

  // ── Two-phase image bank ──────────────────────────────────────────────
  // Harvest stores every image without describing it; the admin culls the grid; Activate then runs
  // vision over what survives. This copy has one job above all others: make "pending" legible. An
  // image the admin can SEE but the bot cannot USE looks like a bug unless the screen says
  // otherwise — and that gap is now the normal state between a crawl and a review.
  mediaTabActivateTitle: {
    en: '{count} image(s) waiting to be indexed',
    he: '{count} תמונות ממתינות לאינדוקס',
  },
  mediaTabActivateBody: {
    en: 'They are stored and shown here, but the bot cannot use them yet. Delete what you do not want first — indexing only runs on what you keep.',
    he: 'הן שמורות ומוצגות כאן, אך הבוט עדיין לא יכול להשתמש בהן. מחק תחילה את מה שאינך רוצה — האינדוקס יפעל רק על מה שתשאיר.',
  },
  mediaTabActivateButton: { en: 'Index kept images ({count})', he: 'אנדקס את התמונות שנשמרו ({count})' },
  mediaTabActivateRunning: { en: 'Indexing…', he: 'מאנדקס…' },
  // Queued, not done. The work runs in the background — saying "indexed" here would be a lie the
  // admin acts on, and the previous synchronous version could only ever have reported a number
  // that was already stale.
  mediaTabActivateQueued: {
    en: 'Indexing {count} image(s) in the background. They will become searchable as they finish — this page updates on its own, and you can leave it.',
    he: 'מאנדקס {count} תמונות ברקע. הן יהפכו לניתנות לחיפוש עם סיום העיבוד — הדף מתעדכן מעצמו, וניתן לעזוב אותו.',
  },
  mediaTabActivateFinished: {
    en: 'Indexing finished. {count} image(s) are searchable.',
    he: 'האינדוקס הסתיים. {count} תמונות ניתנות לחיפוש.',
  },
  mediaTabActivateDone: {
    en: 'Indexed {activated} image(s). The bot can find them now.',
    he: 'אונדקסו {activated} תמונות. הבוט יכול למצוא אותן כעת.',
  },
  mediaTabActivatePartial: {
    en: 'Indexed {activated} of {considered}. {failed} could not be described and are marked failed; {skipped} were skipped.',
    he: 'אונדקסו {activated} מתוך {considered}. {failed} לא ניתנו לתיאור וסומנו כנכשלו; {skipped} דולגו.',
  },
  mediaTabActivateBudget: {
    en: 'Stopped at the run limit — some images are still waiting. Press again to continue.',
    he: 'נעצר במגבלת ההרצה — חלק מהתמונות עדיין ממתינות. לחץ שוב כדי להמשיך.',
  },
  mediaTabActivateDisabled: {
    en: 'Image ingestion is switched off for this tenant, so nothing can be indexed.',
    he: 'קליטת תמונות מושבתת עבור לקוח זה, ולכן לא ניתן לאנדקס דבר.',
  },
  mediaTabActivateFailed: { en: 'Indexing could not start.', he: 'לא ניתן היה להתחיל את האינדוקס.' },
  // Says "busy", never "broken", and names the one action that resolves it. The old wording sent
  // an admin looking for a defect when the server had simply asked them to wait.
  mediaTabActivateRateLimited: {
    en: 'Too many requests at once — nothing was started. Wait a moment and press again.',
    he: 'יותר מדי בקשות בבת אחת — דבר לא הופעל. המתן רגע ולחץ שוב.',
  },
  mediaTabActivateAlreadyRunning: {
    en: 'Indexing is already running for this bank — this page will keep updating as it finishes.',
    he: 'האינדוקס כבר רץ עבור מאגר זה — דף זה ימשיך להתעדכן עם התקדמותו.',
  },
  mediaTabActivateBankBusy: {
    en: 'A {operation} operation is running on this bank. Wait for it to finish, then press again.',
    he: 'פעולת {operation} רצה על מאגר זה. המתן לסיומה ולחץ שוב.',
  },

  // Status chips — worded as what the admin can rely on, not as the internal enum value.
  mediaTabStatusPending: { en: 'Not indexed yet', he: 'עדיין לא אונדקס' },
  mediaTabStatusActive: { en: 'Searchable', he: 'ניתן לחיפוש' },
  mediaTabStatusSuppressed: { en: 'Deleted', he: 'נמחק' },
  mediaTabStatusFailed: { en: 'Could not describe', he: 'לא ניתן לתאר' },

  // Score. Null and 0 must never render alike — see MediaItem.relevanceScore.
  mediaTabScoreLabel: { en: 'Relevance', he: 'רלוונטיות' },
  mediaTabScoreUnjudged: { en: 'Not judged yet', he: 'טרם נשפט' },

  // Filters
  mediaTabFilterLabel: { en: 'Show', he: 'הצג' },
  mediaTabFilterAll: { en: 'All ({count})', he: 'הכל ({count})' },
  mediaTabFilterPending: { en: 'Not indexed ({count})', he: 'לא אונדקסו ({count})' },
  mediaTabFilterActive: { en: 'Searchable ({count})', he: 'ניתנות לחיפוש ({count})' },
  mediaTabFilterDeleted: { en: 'Deleted ({count})', he: 'נמחקו ({count})' },
  mediaTabFilterFailed: { en: 'Failed ({count})', he: 'נכשלו ({count})' },
  mediaTabMinScoreLabel: { en: 'Min relevance', he: 'רלוונטיות מזערית' },
  mediaTabShowingCount: { en: 'showing {shown} of {total}', he: 'מוצגות {shown} מתוך {total}' },
  mediaTabEmptyForFilter: { en: 'No images match this filter.', he: 'אין תמונות שתואמות לסינון זה.' },

  // Restore / purge
  mediaTabRestore: { en: 'Restore', he: 'שחזר' },
  mediaTabRestoreFailed: { en: 'Restore failed.', he: 'השחזור נכשל.' },
  mediaTabRestoreDone: {
    en: 'Restored {count} image(s). Index them to make them searchable again.',
    he: 'שוחזרו {count} תמונות. אנדקס אותן כדי שיהיו ניתנות לחיפוש שוב.',
  },
  mediaTabPurge: { en: 'Delete permanently', he: 'מחק לצמיתות' },
  mediaTabPurgeConfirm: {
    en: 'Permanently delete {count} image(s)?\n\nThis frees the stored bytes, so the preview and the option to restore both go away. The image is still remembered as rejected, so re-indexing will not bring it back.',
    he: 'למחוק לצמיתות {count} תמונות?\n\nפעולה זו משחררת את הבייטים השמורים, ולכן התצוגה המקדימה והאפשרות לשחזר ייעלמו. התמונה עדיין תיזכר כנדחתה, כך שאינדוקס מחדש לא יחזיר אותה.',
  },
  mediaTabPurgeFailed: { en: 'Permanent delete failed.', he: 'המחיקה הצמיתה נכשלה.' },
  mediaTabPurged: { en: '(preview removed)', he: '(התצוגה המקדימה הוסרה)' },
  mediaTabDeletedHint: {
    en: 'These are hidden from the bot. Restore one to use it again, or delete it permanently to free the space.',
    he: 'אלו מוסתרות מהבוט. שחזר תמונה כדי להשתמש בה שוב, או מחק אותה לצמיתות כדי לפנות מקום.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge Banks — Discovery tab
  // ═══════════════════════════════════════════════════════════════════════
  discoveryTabDiscoveryFailed: { en: 'Discovery failed.', he: 'הגילוי נכשל.' },
  discoveryTabRunPartial: {
    en: 'Run completed partially (budget). {count} link(s) graded',
    he: 'הריצה הושלמה חלקית (מגבלה). {count} קישורים דורגו',
  },
  discoveryTabRunComplete: {
    en: 'Discovery complete. {count} link(s) graded',
    he: 'הגילוי הושלם. {count} קישורים דורגו',
  },
  discoveryTabFlaggedReview: { en: ', {flagged} flagged for review', he: ', {flagged} סומנו לבדיקה' },
  discoveryTabFlagged: { en: ', {flagged} flagged', he: ', {flagged} סומנו' },
  discoveryTabQueued: {
    en: 'Discovery queued — fetching pages and grading in the background. This may take up to 3 minutes for depth 3 on a large site.',
    he: 'הגילוי בתור — מביא דפים ומדרג ברקע. זה עשוי להימשך עד 3 דקות עבור עומק 3 באתר גדול.',
  },
  discoveryTabUpdateLinkFailed: { en: 'Failed to update link.', he: 'עדכון הקישור נכשל.' },
  discoveryTabSaved: {
    en: 'Saved. {sel} link(s) marked for indexing, {total} link(s) recorded in total.',
    he: 'נשמר. {sel} קישורים סומנו להוספה לאינדקס, {total} קישורים נרשמו בסך הכל.',
  },
  discoveryTabSaveFailed: { en: 'Save failed.', he: 'השמירה נכשלה.' },
  discoveryTabSelectAtLeastOne: { en: 'Select at least one link before indexing.', he: 'בחר לפחות קישור אחד לפני ההוספה לאינדקס.' },
  discoveryTabQueuedIndex: {
    en: 'Saved + queued {count} link(s) for indexing. The chunk count on this bank will rise as parsing + embedding completes — you can refresh to track progress.',
    he: 'נשמר + הוכנסו לתור {count} קישורים להוספה לאינדקס. מספר המקטעים במאגר זה יעלה ככל שהניתוח + ההטמעה יושלמו — ניתן לרענן כדי לעקוב אחר ההתקדמות.',
  },
  discoveryTabIndexFailed: { en: 'Index failed.', he: 'ההוספה לאינדקס נכשלה.' },
  discoveryTabBulkDeleteConfirm: {
    en: 'Delete {count} link(s)? They\'ll be removed from this view; the next discovery on this site can re-grade them.',
    he: 'למחוק {count} קישורים? הם יוסרו מתצוגה זו; הגילוי הבא באתר זה יכול לדרג אותם מחדש.',
  },
  discoveryTabDeleted: { en: 'Deleted {count} link(s).', he: 'נמחקו {count} קישורים.' },
  discoveryTabBulkDeleteFailed: { en: 'Bulk delete failed.', he: 'המחיקה הקבוצתית נכשלה.' },
  discoveryTabRegrading: { en: 'Re-grading {count} link(s) in the background…', he: 'מדרג מחדש {count} קישורים ברקע…' },
  discoveryTabRegraded: {
    en: 'Re-graded {count} link(s). Selection / deletion state preserved.',
    he: 'דורגו מחדש {count} קישורים. מצב הבחירה / המחיקה נשמר.',
  },
  discoveryTabRegradeStillRunning: {
    en: 'Re-grade is still running — refresh in a moment to see the updated grades.',
    he: 'הדירוג מחדש עדיין פועל — רענן בעוד רגע כדי לראות את הדירוגים המעודכנים.',
  },
  discoveryTabRegradeFailedStart: { en: 'Re-grade failed to start.', he: 'הפעלת הדירוג מחדש נכשלה.' },
  discoveryTabRegradeFailed: { en: 'Re-grading failed.', he: 'הדירוג מחדש נכשל.' },
  discoveryTabUrlLabel: { en: 'URL', he: 'כתובת URL' },
  discoveryTabUrlPlaceholder: { en: 'https://example.com/articles', he: 'https://example.com/articles' },
  discoveryTabDepthLabel: { en: 'Depth', he: 'עומק' },
  discoveryTabDepth0: { en: '0 — root only', he: '0 — שורש בלבד' },
  discoveryTabDepth1: { en: '1 — root + links', he: '1 — שורש + קישורים' },
  discoveryTabDepth3: { en: '3 (max)', he: '3 (מקסימום)' },
  discoveryTabCutoffLabel: { en: 'Cutoff', he: 'סף' },
  discoveryTabDiscovering: { en: 'Discovering…', he: 'מגלה…' },
  discoveryTabRunDiscovery: { en: 'Run discovery', he: 'הפעל גילוי' },
  discoveryTabSameDomain: { en: 'Only crawl pages on the same domain', he: 'סרוק רק דפים באותו דומיין' },
  discoveryTabIncludeSubdomainsPre: { en: 'Include subdomains (e.g.', he: 'כלול תת-דומיינים (לדוגמה' },
  discoveryTabIncludeSubdomainsPost: { en: ')', he: ')' },
  discoveryTabThresholdLabel: {
    // OP-318 (OP-166 T9.7 completion): "Threshold"→"Cutoff", "grade"→"relevance score" (display only)
    en: 'Cutoff (pre-selects rows with relevance score ≥ this):',
    he: 'סף (בוחר מראש שורות עם ציון רלוונטיות ≥ ערך זה):',
  },
  discoveryTabQueueing: { en: 'Queueing…', he: 'מכניס לתור…' },
  discoveryTabSaveIndex: { en: 'Save + Index selected', he: 'שמור + הוסף נבחרים לאינדקס' },
  discoveryTabShow: { en: 'Show:', he: 'הצג:' },
  discoveryTabFilterAll: { en: 'All ({count})', he: 'הכל ({count})' },
  discoveryTabFilterSelected: { en: 'Selected only ({count})', he: 'נבחרים בלבד ({count})' },
  discoveryTabFilterUnselected: { en: 'Not selected ({count})', he: 'לא נבחרו ({count})' },
  discoveryTabFilterFresh: { en: 'Fresh this run ({count})', he: 'חדשים בריצה זו ({count})' },
  discoveryTabFilterInherited: { en: 'From prior runs ({count})', he: 'מריצות קודמות ({count})' },
  discoveryTabViewThreshold: { en: 'View cutoff ≥', he: 'סף תצוגה ≥' },
  discoveryTabShowing: { en: 'showing {n} / {total}', he: 'מציג {n} / {total}' },
  discoveryTabBulkSelected: { en: '{count} selected', he: '{count} נבחרו' },
  discoveryTabRegradeSelected: { en: 'Re-grade selected', he: 'דרג מחדש נבחרים' },
  discoveryTabDeleteSelected: { en: 'Delete selected', he: 'מחק נבחרים' },
  discoveryTabClear: { en: 'Clear', he: 'נקה' },
  discoveryTabBulkSelectTitle: { en: 'Bulk-select for delete / re-grade', he: 'בחירה קבוצתית למחיקה / דירוג מחדש' },
  discoveryTabSelectAllTitle: {
    en: 'Select / clear all {count} visible row(s)',
    he: 'בחר / נקה את כל {count} השורות הגלויות',
  },
  discoveryTabPreselectTitle: { en: 'Pre-select for indexing', he: 'בחירה מראש להוספה לאינדקס' },
  discoveryTabIncludeIndexingTitle: { en: 'Include in indexing', he: 'כלול בהוספה לאינדקס' },
  discoveryTabColRelevance: { en: 'Relevance score', he: 'ציון רלוונטיות' },
  discoveryTabColUrl: { en: 'URL', he: 'כתובת URL' },
  discoveryTabColDescription: { en: 'Description', he: 'תיאור' },
  discoveryTabColCrawlDepth: { en: 'Crawl depth', he: 'עומק סריקה' },
  discoveryTabColActions: { en: 'Actions', he: 'פעולות' },
  discoveryTabInheritedTitle: {
    en: 'Carried over from a previous discovery run for this bank — not re-graded.',
    he: 'הועבר מריצת גילוי קודמת עבור מאגר זה — לא דורג מחדש.',
  },
  discoveryTabInheritedBadge: { en: 'from earlier scan', he: 'מסריקה קודמת' },
  discoveryTabRemove: { en: 'Remove', he: 'הסר' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Learning page
  // ═══════════════════════════════════════════════════════════════════════
  learningLoadingAgents: { en: 'Loading agents...', he: 'טוען סוכנים...' },
  learningTitle: { en: 'Learning', he: 'למידה' },
  learningIntro: {
    en: 'Backfill agent Memory from past conversations, run the learning batch on demand, and manage each agent\'s system-managed Learning Knowledge bank.',
    he: 'מלא את זיכרון הסוכן משיחות עבר, הרץ את מקבץ הלמידה לפי דרישה, ונהל את מאגר ידע הלמידה המנוהל-מערכת של כל סוכן.',
  },
  learningBankLoadError: { en: 'Failed to load learning bank.', he: 'טעינת מאגר הלמידה נכשלה.' },
  learningMigrateJobFailed: { en: 'Migration job failed.', he: 'משימת ההעברה נכשלה.' },
  learningMigrateReadFailed: { en: 'Failed to read migration progress.', he: 'קריאת התקדמות ההעברה נכשלה.' },
  learningMigrateStillRunning: { en: 'Migration is still running — refresh in a moment.', he: 'ההעברה עדיין פועלת — רענן בעוד רגע.' },
  learningMigrateStartFailed: { en: 'Failed to start migration.', he: 'הפעלת ההעברה נכשלה.' },
  // "Done — 0/0" read as success for two full runs while the feature was mining nothing.
  // A finished job that kept no conversation has to say so, and say why.
  learningMigrateNothingMined: {
    en: 'Looked at {seen} conversation(s) and learned from none of them.',
    he: 'נבדקו {seen} שיחות ולא נלמד דבר מאף אחת מהן.',
  },
  learningMigrateNoSessions: {
    en: 'No recorded conversations were found for this tenant.',
    he: 'לא נמצאו שיחות מוקלטות עבור ארגון זה.',
  },
  learningMigrateSkipUnattributable: {
    en: 'could not be attributed to a bot',
    he: 'לא ניתן היה לשייך לבוט',
  },
  learningMigrateSkipOtherAgent: { en: 'belong to another bot', he: 'שייכות לבוט אחר' },
  learningMigrateSkipOperatorTag: { en: 'marked "skip"', he: 'סומנו "דלג"' },
  learningMigrateSkipAlreadyMined: { en: 'already learned earlier', he: 'נלמדו כבר בעבר' },
  learningMigrateSkipSynthesisFailed: { en: 'failed while extracting', he: 'נכשלו בעת החילוץ' },
  // Enqueue-level counts. The fact/behaviour verdict is decided asynchronously by
  // LearningService and is shown in the decision trail above, not here — the previous
  // wording promised proposal/fact/dropped counts the server always returned as 0.
  learningRunSummary: {
    en: 'Sent {processed} · {accepted} accepted · {rejected} rejected · {left} to retry',
    he: 'נשלחו {processed} · {accepted} התקבלו · {rejected} נדחו · {left} לניסיון חוזר',
  },
  learningRunNothingToPromote: {
    en: 'Nothing new to learn — no high-priority memory was waiting for these agents.',
    he: 'אין מה ללמוד — לא היו פריטי זיכרון בעדיפות גבוהה עבור סוכנים אלה.',
  },
  learningRunRejectedWarning: {
    en: '{n} item(s) could not be saved and won\'t be tried again — see the table below for details.',
    he: '{n} פריט(ים) לא נשמרו ולא ייבדקו שוב — לפרטים ראו את הטבלה למטה.',
  },
  learningRunFailedSuffix: { en: ' · {failures} agent{s} failed', he: ' · {failures} סוכנים נכשלו' },
  learningRunError: { en: '{failures} agent run{s} failed.', he: 'ריצות של {failures} סוכנים נכשלו.' },
  learningBankToggleError: { en: 'Failed to toggle learning bank.', he: 'החלפת מצב מאגר הלמידה נכשלה.' },
  learningAgentsHeader: { en: 'Agents', he: 'סוכנים' },
  learningAgentsHint: { en: 'Select one or more agents to act on.', he: 'בחר סוכן אחד או יותר לביצוע פעולה.' },
  learningNoAgents: { en: 'No agents configured for this tenant yet.', he: 'אין עדיין סוכנים מוגדרים עבור ארגון זה.' },
  learningSelectAgentAria: { en: 'Select agent {name}', he: 'בחר את הסוכן {name}' },
  learningInactive: { en: '(inactive)', he: '(לא פעיל)' },
  learningMigrateHeader: { en: 'Teach from past chats', he: 'למד משיחות עבר' },
  learningMigrateHint: {
    en: 'Replays the tenant\'s recorded conversations through the memory synthesis pipeline for the selected agents, so their Memory tiers are backfilled from history instead of waiting for new live turns.',
    he: 'משחזר את השיחות המוקלטות של הארגון דרך צינור סינתזת הזיכרון עבור הסוכנים הנבחרים, כך ששכבות הזיכרון שלהם מתמלאות מההיסטוריה במקום להמתין לתורות חדשים בזמן אמת.',
  },
  learningMigrating: { en: 'Migrating…', he: 'מעביר…' },
  learningMigrateButton: { en: 'Teach from past chats', he: 'למד משיחות עבר' },
  learningMigrateDone: { en: 'Done — ', he: 'הושלם — ' },
  learningMigrateFailed: { en: 'Failed — ', he: 'נכשל — ' },
  learningMigrateRunning: { en: 'Running — ', he: 'פועל — ' },
  learningMigrateProgress: {
    en: '{processed}/{total} conversations · {notes} notes written',
    he: '{processed}/{total} שיחות · {notes} הערות נכתבו',
  },
  learningRunHeader: { en: 'Update learning now', he: 'עדכן למידה כעת' },
  learningRunHint: {
    en: 'Runs the learning batch on demand for each selected agent instead of waiting for the nightly run. Behavior surfaces as review-gated proposals on each agent; facts go to its Learning Bank.',
    he: 'מריץ את מקבץ הלמידה לפי דרישה עבור כל סוכן נבחר במקום להמתין לריצה הלילית. התנהגות מוצגת כהצעות הכפופות לבדיקה עבור כל סוכן; עובדות נשלחות למאגר הלמידה שלו.',
  },
  learningRunning: { en: 'Running…', he: 'פועל…' },
  learningRunButton: { en: 'Update learning now', he: 'עדכן למידה כעת' },
  learningBankHeader: { en: 'Learning Bank', he: 'מאגר למידה' },
  learningBankHint: {
    en: 'Each agent has a system-managed Learning Knowledge bank. It is non-deletable — turn it on or off below.',
    he: 'לכל סוכן יש מאגר ידע למידה מנוהל-מערכת. לא ניתן למחוק אותו — הפעל או כבה אותו למטה.',
  },
  learningBankSelectPrompt: { en: 'Select an agent to view its Learning bank.', he: 'בחר סוכן כדי לצפות במאגר הלמידה שלו.' },
  learningBankStats: { en: ' · {chunks} chunks · {sources} sources', he: ' · {chunks} מקטעים · {sources} מקורות' },
  learningBankDisableFor: { en: 'Disable learning bank for {name}', he: 'השבת את מאגר הלמידה עבור {name}' },
  learningBankEnableFor: { en: 'Enable learning bank for {name}', he: 'הפעל את מאגר הלמידה עבור {name}' },
  learningBankOn: { en: 'On', he: 'פעיל' },
  learningBankOff: { en: 'Off', he: 'כבוי' },
  learningMarkedHeader: { en: 'Marked chats', he: 'שיחות מסומנות' },
  learningRefreshing: { en: 'Refreshing…', he: 'מרענן…' },
  learningRefresh: { en: 'Refresh', he: 'רענן' },
  learningMarkedHint1: {
    en: 'Sessions a human agent has explicitly tagged in the agent console.',
    he: 'שיחות שנציג אנושי תייג במפורש במסוף הנציג.',
  },
  learningMarkedForceLearn: { en: ' Force-learn', he: ' למידה כפויה' },
  learningMarkedHint2: {
    en: ' sessions are synthesized into Memory even when low-signal; ',
    he: ' שיחות מסונתזות לזיכרון גם כאשר האות חלש; ',
  },
  learningMarkedSkip: { en: 'skip', he: 'דילוג' },
  learningMarkedHint3: {
    en: ' sessions are excluded from learning. The note (if any) is prepended to the synthesized material.',
    he: ' שיחות אינן נכללות בלמידה. ההערה (אם קיימת) מתווספת בתחילת החומר המסונתז.',
  },
  learningTaggedLoading: { en: 'Loading tagged sessions…', he: 'טוען שיחות מסומנות…' },
  learningNoTagged: { en: 'No tagged sessions yet.', he: 'אין עדיין שיחות מסומנות.' },
  learningNeverLearn: { en: 'Never learn', he: 'לעולם אל תלמד' },
  learningAlwaysLearn: { en: 'Always learn', he: 'תמיד למד' },
  learningCustomerLabel: { en: 'customer', he: 'לקוח' },
  learningAgentLabel: { en: 'agent', he: 'נציג' },
  learningFlipAria: { en: 'Switch {id} to {tag}', he: 'החלף את {id} ל-{tag}' },
  learningFlipToAlways: { en: '→ Always learn', he: '→ תמיד למד' },
  learningFlipToNever: { en: '→ Never learn', he: '→ לעולם אל תלמד' },
  learningClearAria: { en: 'Clear learning tag on {id}', he: 'נקה את תג הלמידה של {id}' },
  learningClear: { en: 'Clear', he: 'נקה' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Learning page — Activity (the decision trail)
  //
  //  Copy here is deliberately blunt about FAILURE. The learning pipeline was
  //  silently broken for its entire life: every candidate was rejected at a
  //  middleware tenant gate, the rejection was recorded only in Mongo, and
  //  every feature flag still read "healthy". A rejected candidate must never
  //  read like "there was nothing to learn".
  // ═══════════════════════════════════════════════════════════════════════
  learningActivityHeader: { en: 'Learning activity', he: 'פעילות למידה' },
  learningActivityHint: {
    en: 'Every learning candidate the bots produced and what actually happened to it — the fact verdict, the behavior verdict, and the reason when nothing was written. If this list is empty, no candidate ever reached the classifier; if rows are marked failed, candidates arrived and were rejected.',
    he: 'כל מועמד ללמידה שהבוטים הפיקו ומה קרה לו בפועל — הכרעת העובדה, הכרעת ההתנהגות, והסיבה כאשר דבר לא נכתב. אם הרשימה ריקה, אף מועמד לא הגיע למסווג; אם שורות מסומנות כנכשלו, מועמדים הגיעו ונדחו.',
  },
  learningActivityLoading: { en: 'Loading learning activity…', he: 'טוען פעילות למידה…' },
  learningActivityFilterAgentAria: { en: 'Filter learning activity by bot', he: 'סנן פעילות למידה לפי בוט' },
  learningActivityAllAgents: { en: 'All bots', he: 'כל הבוטים' },
  learningActivityFilterSourceAria: { en: 'Filter learning activity by source', he: 'סנן פעילות למידה לפי מקור' },
  learningActivityAllSources: { en: 'All sources', he: 'כל המקורות' },
  learningActivitySourceConsultation: { en: 'Consultation', he: 'התייעצות' },
  learningActivitySourceTakeover: { en: 'Takeover', he: 'השתלטות' },
  learningActivitySourceFeedback: { en: 'Feedback', he: 'משוב' },
  learningActivitySourceConversation: { en: 'Conversation', he: 'שיחה' },
  learningActivitySourceEmailDraftEdit: { en: 'Email draft edit', he: 'עריכת טיוטת אימייל' },
  learningActivityColTime: { en: 'When (UTC)', he: 'מתי (UTC)' },
  learningActivityColSource: { en: 'Source', he: 'מקור' },
  learningActivityColCandidate: { en: 'Candidate', he: 'מועמד' },
  learningActivityColFact: { en: 'Fact verdict', he: 'הכרעת עובדה' },
  learningActivityColBehavior: { en: 'Behavior verdict', he: 'הכרעת התנהגות' },
  learningActivityColGate: { en: 'Usefulness gate', he: 'בדיקת תועלת' },
  learningActivityUtc: { en: 'UTC', he: 'UTC' },
  learningActivitySummary: {
    en: 'Showing {loaded} of {total} · {landed} landed · {failed} failed · {none} nothing to learn · {routerOff} router off',
    he: 'מוצגים {loaded} מתוך {total} · {landed} נכתבו · {failed} נכשלו · {none} אין מה ללמוד · {routerOff} נתב כבוי',
  },
  learningActivitySummaryFailWarn: {
    en: 'Rejected candidates reached the pipeline and were thrown away — read the reason column before assuming the bots had nothing to learn.',
    he: 'מועמדים שנדחו הגיעו לצינור והושמטו — קרא את עמודת הסיבה לפני שתניח שלבוטים לא היה מה ללמוד.',
  },
  learningActivityAllRejected: {
    en: 'Every loaded candidate was REJECTED — nothing reached a Learning bank or a memory tier. That is a systemic failure, not "nothing to learn".',
    he: 'כל המועמדים שנטענו נדחו — דבר לא הגיע למאגר למידה או לשכבת זיכרון. זו תקלה מערכתית, לא "אין מה ללמוד".',
  },
  learningActivityNeverAttempted: {
    en: 'No learning has been attempted yet — not one candidate has reached the classifier for this tenant. Mark a chat for learning or run "Update learning now" to produce the first rows.',
    he: 'עדיין לא נעשה ניסיון למידה — אף מועמד לא הגיע למסווג עבור ארגון זה. סמן שיחה ללמידה או הרץ "עדכן למידה כעת" כדי לייצר את השורות הראשונות.',
  },
  learningActivityNoMatch: {
    en: 'No learning activity matches these filters. Clear them to see the whole trail.',
    he: 'אין פעילות למידה התואמת למסננים אלה. נקה אותם כדי לראות את כל המסלול.',
  },
  learningActivityBadgeLanded: { en: 'Landed', he: 'נכתב' },
  learningActivityBadgeFailed: { en: 'Failed', he: 'נכשל' },
  learningActivityBadgeNothing: { en: 'Nothing to learn', he: 'אין מה ללמוד' },
  learningActivityBadgeRouterOff: { en: 'Router off', he: 'נתב כבוי' },
  learningActivityRouterOffNote: {
    en: 'The learning router was switched OFF when this candidate arrived — it was never classified.',
    he: 'נתב הלמידה היה כבוי כאשר מועמד זה הגיע — הוא לא סווג כלל.',
  },
  learningActivityFactWritten: { en: 'Fact written', he: 'עובדה נכתבה' },
  learningActivityFactUsefulNotWritten: { en: 'Useful fact — nothing written', he: 'עובדה מועילה — דבר לא נכתב' },
  learningActivityFactNotUseful: { en: 'Not a fact', he: 'אינו עובדה' },
  learningActivityFactFailed: { en: 'Fact rejected', he: 'העובדה נדחתה' },
  learningActivityBehaviorMemory: { en: 'Memory written', he: 'זיכרון נכתב' },
  learningActivityBehaviorReview: { en: 'Review note', he: 'הערה לבדיקה' },
  learningActivityBehaviorNothingWritten: { en: 'Behavior — nothing written', he: 'התנהגות — דבר לא נכתב' },
  learningActivityBehaviorNot: { en: 'Not a behavior', he: 'אינו התנהגות' },
  learningActivityBehaviorFailed: { en: 'Behavior rejected', he: 'ההתנהגות נדחתה' },
  learningActivityGateKept: { en: 'Kept', he: 'נשמר' },
  learningActivityGateDropped: { en: 'Dropped', he: 'הושמט' },
  learningActivityGateNotRun: { en: 'Gate did not run', he: 'הבדיקה לא רצה' },
  learningActivityBankLabel: { en: 'bank', he: 'מאגר' },
  learningActivitySessionLabel: { en: 'chat', he: 'שיחה' },
  learningActivityFormLabel: { en: 'form', he: 'צורה' },
  learningActivityScopeLabel: { en: 'scope', he: 'היקף' },
  learningActivityTierLabel: { en: 'tier', he: 'שכבה' },
  learningActivityLoadMore: { en: 'Load more ({loaded} of {total})', he: 'טען עוד ({loaded} מתוך {total})' },
  learningActivityLoadingMore: { en: 'Loading…', he: 'טוען…' },
  learningActivityAllLoaded: { en: 'All {total} decisions loaded.', he: 'כל {total} ההכרעות נטענו.' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Learning page — provision Learning banks for every bot
  // ═══════════════════════════════════════════════════════════════════════
  learningProvisionButton: { en: 'Open Learning KB for every bot', he: 'פתח מאגר למידה לכל בוט' },
  learningProvisioning: { en: 'Opening…', he: 'פותח…' },
  // NOTE: this copy deliberately avoids the word "delete" — LearningPage.test.tsx
  // pins that the Learning-bank panel offers NO delete affordance by asserting
  // no /delete/i text anywhere on the page (the bank is non-deletable).
  learningProvisionHint: {
    en: 'Makes sure every AI bot in this tenant has its Learning Knowledge bank opened — a bot without one has nowhere to put what it learns, which looks exactly like a bot that learned nothing. Safe to re-run at any time: bots that already have a bank are simply re-reported, nothing is re-created, and no knowledge is removed.',
    he: 'מוודא שלכל בוט AI בארגון זה נפתח מאגר ידע למידה — לבוט בלי מאגר אין לאן לכתוב את מה שלמד, וזה נראה בדיוק כמו בוט שלא למד דבר. בטוח להרצה חוזרת בכל עת: בוטים שכבר יש להם מאגר מדווחים מחדש, דבר אינו נוצר מחדש, ואף ידע אינו מוסר.',
  },
  learningProvisionSummary: {
    en: '{total} bots · {ai} AI bots · {ok} with a Learning bank · {failed} failed',
    he: '{total} בוטים · {ai} בוטי AI · {ok} עם מאגר למידה · {failed} נכשלו',
  },
  learningProvisionAllOk: { en: 'Every AI bot has a Learning bank.', he: 'לכל בוט AI יש מאגר למידה.' },
  learningProvisionPartial: {
    en: 'PARTIAL SUCCESS — {failed} of {ai} AI bots did NOT get a Learning bank. Each failure is listed below with its reason; re-running is safe.',
    he: 'הצלחה חלקית — {failed} מתוך {ai} בוטי AI לא קיבלו מאגר למידה. כל כשל מפורט להלן עם הסיבה; הרצה חוזרת בטוחה.',
  },
  learningProvisionNoAiAgents: {
    en: 'This tenant has no AI bots, so there is no Learning bank to open.',
    he: 'לארגון זה אין בוטי AI, ולכן אין מאגר למידה לפתוח.',
  },
  learningProvisionBankOpen: { en: 'Bank open', he: 'מאגר פתוח' },
  learningProvisionNoBank: { en: 'No bank', he: 'אין מאגר' },
  learningProvisionFailed: { en: 'Could not open the Learning banks.', he: 'לא ניתן היה לפתוח את מאגרי הלמידה.' },
  learningProvisionTimeout: {
    en: 'Still working server-side — the backfill outlived the request. It is idempotent, so re-running in a moment is safe and will report the final state.',
    he: 'עדיין פועל בצד השרת — המילוי חרג ממשך הבקשה. הפעולה חזרתית ובטוחה, כך שהרצה חוזרת בעוד רגע תדווח על המצב הסופי.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Bot Entities — list
  //
  //  OP-563 — one object, four names: the route says /bot-entities, the key prefix says
  //  botEntity, the tab says "Bot Attachments" and the heading used to say "Bots (Agent +
  //  Channel)". CLAUDE.md settles it: the user-facing name is "Bot Attachments". Only the
  //  VISIBLE strings move — the route and the key prefix stay put so deep links and the
  //  i18n contract keep working.
  // ═══════════════════════════════════════════════════════════════════════
  botEntityListLoading: { en: 'Loading bot attachments...', he: 'טוען שיוכי בוט...' },
  botEntityListTitle: { en: 'Bot Attachments', he: 'שיוכי בוט' },
  botEntityListNewBot: { en: '+ New Bot Attachment', he: '+ שיוך בוט חדש' },
  botEntityListLoadError: { en: 'Failed to load bot attachments.', he: 'טעינת שיוכי הבוט נכשלה.' },
  botEntityListActivateFailed: { en: 'Failed to activate bot attachment.', he: 'הפעלת שיוך הבוט נכשלה.' },
  botEntityListDeactivateFailed: { en: 'Failed to deactivate bot attachment.', he: 'השבתת שיוך הבוט נכשלה.' },
  botEntityListDeleteConfirm: {
    en: 'Delete this bot attachment? This will remove the Agent+Channel link.',
    he: 'למחוק את שיוך הבוט הזה? פעולה זו תסיר את הקישור בין הסוכן לערוץ.',
  },
  botEntityListDeleteFailed: { en: 'Failed to delete bot attachment.', he: 'מחיקת שיוך הבוט נכשלה.' },
  botEntityListColBotName: { en: 'Attachment Name', he: 'שם השיוך' },
  botEntityListColAgentType: { en: 'Agent Type', he: 'סוג סוכן' },
  botEntityListColChannel: { en: 'Channel', he: 'ערוץ' },
  botEntityListColStatus: { en: 'Status', he: 'סטטוס' },
  botEntityListColActions: { en: 'Actions', he: 'פעולות' },
  botEntityListActive: { en: 'Active', he: 'פעיל' },
  botEntityListInactive: { en: 'Inactive', he: 'לא פעיל' },
  botEntityListActivate: { en: 'Activate', he: 'הפעל' },
  botEntityListDeactivate: { en: 'Deactivate', he: 'השבת' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Bot Entities — create / edit form
  // ═══════════════════════════════════════════════════════════════════════
  botEntityFormLoading: { en: 'Loading...', he: 'טוען...' },
  botEntityFormLoadError: { en: 'Failed to load data.', he: 'טעינת הנתונים נכשלה.' },
  botEntityFormUpdateFailed: { en: 'Failed to update bot attachment.', he: 'עדכון שיוך הבוט נכשל.' },
  botEntityFormCreateFailed: { en: 'Failed to create bot attachment.', he: 'יצירת שיוך הבוט נכשלה.' },
  // OP-563 — "Bot Attachment" everywhere the operator can read it (see the list block above).
  botEntityFormEditTitle: { en: 'Edit Bot Attachment', he: 'עריכת שיוך בוט' },
  botEntityFormCreateTitle: { en: 'Create Bot Attachment', he: 'יצירת שיוך בוט' },
  botEntityFormNameLabel: { en: 'Attachment Name', he: 'שם השיוך' },
  botEntityFormNamePlaceholder: { en: 'e.g. DummyBot-WebChat', he: 'לדוגמה DummyBot-WebChat' },
  botEntityFormAgentLabel: { en: 'Agent', he: 'סוכן' },
  botEntityFormSelectAgent: { en: 'Select an agent...', he: 'בחר סוכן...' },
  botEntityFormAgentType: { en: 'Type:', he: 'סוג:' },
  botEntityFormChannelLabel: { en: 'Channel', he: 'ערוץ' },
  botEntityFormSelectChannel: { en: 'Select a channel...', he: 'בחר ערוץ...' },
  botEntityFormHasActiveBot: { en: '(has active bot)', he: '(יש בוט פעיל)' },
  botEntityFormChannelWarnPre: {
    en: '⚠️ This channel already has an active bot. You can create this bot as',
    he: '⚠️ לערוץ זה כבר יש בוט פעיל. ניתן ליצור בוט זה במצב',
  },
  botEntityFormInactiveWord: { en: 'Inactive', he: 'לא פעיל' },
  botEntityFormChannelWarnPost: {
    en: ' and activate it later after deactivating the other bot.',
    he: ' ולהפעיל אותו מאוחר יותר לאחר השבתת הבוט האחר.',
  },
  botEntityFormCreateActive: { en: 'Create as Active', he: 'צור כפעיל' },
  botEntityFormActiveHint: {
    en: 'Active bots handle messages on their channel. Only one bot per channel can be active. Inactive bots are saved but don\'t receive messages until activated.',
    he: 'בוטים פעילים מטפלים בהודעות בערוץ שלהם. רק בוט אחד לכל ערוץ יכול להיות פעיל. בוטים לא פעילים נשמרים אך אינם מקבלים הודעות עד להפעלתם.',
  },
  botEntityFormSaving: { en: 'Saving...', he: 'שומר...' },
  botEntityFormCreating: { en: 'Creating...', he: 'יוצר...' },
  botEntityFormSaveChanges: { en: 'Save Changes', he: 'שמור שינויים' },
  botEntityFormCreateBot: { en: 'Create Bot Attachment', he: 'צור שיוך בוט' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Channels — list + snippet modal
  // ═══════════════════════════════════════════════════════════════════════
  channelListLoading: { en: 'Loading channels...', he: 'טוען ערוצים...' },
  channelListTitle: { en: 'Channels', he: 'ערוצים' },
  channelListNewChannel: { en: '+ New Channel', he: '+ ערוץ חדש' },
  channelListDeleteConfirm: { en: 'Delete this channel?', he: 'למחוק ערוץ זה?' },
  channelListDeleteFailed: { en: 'Failed to delete channel.', he: 'מחיקת הערוץ נכשלה.' },
  channelListRegenerateFailed: { en: 'Failed to regenerate secret.', he: 'יצירת סוד מחדש נכשלה.' },
  channelListCopied: { en: 'Copied!', he: 'הועתק!' },
  channelListCopySnippet: { en: 'Copy Snippet', he: 'העתק קטע קוד' },
  channelListCopyNewSecret: { en: 'Copy New Secret', he: 'העתק סוד חדש' },
  channelListCopyExample: { en: 'Copy Example', he: 'העתק דוגמה' },
  channelListEmbedSnippetAria: { en: 'Embed Snippet', he: 'קטע קוד להטמעה' },
  channelListClose: { en: 'Close', he: 'סגור' },
  channelListEmbedSnippetLabel: { en: 'Embed Snippet —', he: 'קטע קוד להטמעה —' },
  channelListIdentityMode: { en: 'Identity mode:', he: 'מצב זיהוי:' },
  channelListSecretSet: { en: 'Secret set:', he: 'סוד הוגדר:' },
  channelListYes: { en: 'Yes', he: 'כן' },
  channelListNo: { en: 'No', he: 'לא' },
  channelListWidgetHost: { en: 'Widget host:', he: 'מארח הווידג׳ט:' },
  channelListHmacOn: { en: 'HMAC ON', he: 'HMAC פעיל' },
  channelListIntegrationGuide: {
    en: '📖 Step-by-step installation guide (send this link to the customer)',
    he: '📖 מדריך התקנה שלב-אחר-שלב (שלח קישור זה ללקוח)',
  },
  channelListNewSecretWarning: {
    en: 'New Channel Secret — Store server-side now — it will NOT be shown again',
    he: 'סוד ערוץ חדש — אחסן בצד השרת כעת — הוא לא יוצג שוב',
  },
  channelListHmacExampleTitle: { en: 'Server-side HMAC example (Node.js)', he: 'דוגמת HMAC בצד השרת (Node.js)' },
  channelListRegenerating: { en: 'Regenerating...', he: 'יוצר מחדש...' },
  channelListRegenerateSecret: { en: 'Regenerate Secret', he: 'צור סוד מחדש' },
  channelListRegenerateHint: {
    en: 'Existing integrations stop verifying until the new secret is deployed.',
    he: 'אינטגרציות קיימות יפסיקו לאמת עד לפריסת הסוד החדש.',
  },
  channelListRegenerateConfirm: {
    en: 'Regenerate the channel secret?\n\nExisting integrations stop verifying until the new secret is deployed server-side.',
    he: 'ליצור מחדש את סוד הערוץ?\n\nאינטגרציות קיימות יפסיקו לאמת עד לפריסת הסוד החדש בצד השרת.',
  },
  channelListColName: { en: 'Name', he: 'שם' },
  channelListColType: { en: 'Type', he: 'סוג' },
  channelListColStatus: { en: 'Status', he: 'סטטוס' },
  channelListColWidgetId: { en: 'Widget ID', he: 'מזהה ווידג׳ט' },
  channelListColEnabled: { en: 'Enabled', he: 'מופעל' },
  channelStatusEnabled: { en: 'Enabled', he: 'מופעל' },
  channelStatusDisabled: { en: 'Disabled — this channel accepts no new chats', he: 'מושבת — הערוץ אינו מקבל צ׳אטים חדשים' },
  // OP-545 — an unattached channel replies to nobody, and used to be invisible in this list.
  channelListColAgent: { en: 'Agent', he: 'סוכן' },
  // OP-534 — the channel kill switch. Before this the only per-channel stop the pollers honour
  // was unreachable from the product, so containing one mailbox meant stopping every service.
  channelListDisable: { en: 'Disable', he: 'השבת' },
  channelListEnable: { en: 'Enable', he: 'הפעל' },
  channelListEdit: { en: 'Edit', he: 'עריכה' },
  channelListDisableTitle: { en: 'Disable this channel?', he: 'להשבית את הערוץ?' },
  channelListEnableTitle: { en: 'Enable this channel?', he: 'להפעיל את הערוץ?' },
  channelListDisableConfirm: {
    en: 'Disable channel "{name}"? Email and WhatsApp stop reading the mailbox and sending replies; WebChat stops on the next message - customers in open chats are refused and any bot reply still in flight is dropped. The state is re-read on every send and cached for up to 30 seconds. Nothing is deleted.',
    he: 'להשבית את הערוץ "{name}"? אימייל ו-WhatsApp מפסיקים לקרוא את התיבה ולשלוח תשובות; WebChat נעצר בהודעה הבאה - לקוחות בשיחות פתוחות נדחים ותשובת בוט שעדיין בדרך נזרקת. המצב נקרא מחדש בכל שליחה ונשמר במטמון עד 30 שניות. דבר אינו נמחק.',
  },
  channelListEnableConfirm: {
    en: 'Enable channel "{name}"? It resumes receiving and replying on the next message (up to 30 seconds for cached state). Make sure the bot attachment is the one you intend before confirming.',
    he: 'להפעיל את הערוץ "{name}"? הוא יחזור לקבל ולענות בהודעה הבאה (עד 30 שניות למצב במטמון). ודאו שהבוט המחובר הוא זה שרציתם לפני האישור.',
  },
  channelListDisableHint: { en: 'Stop this channel receiving and replying', he: 'עצירת קבלה ומענה בערוץ' },
  channelListEnableHint: { en: 'Resume receiving and replying on this channel', he: 'חידוש קבלה ומענה בערוץ' },
  channelListToggleFailed: { en: 'Could not change the channel state', he: 'שינוי מצב הערוץ נכשל' },
  // OP-547 — the edit form used to accept agent/channel changes and silently discard them.
  botEntityFormRebindHint: {
    en: 'An attachment is the agent-and-channel pair, so these cannot be changed here — saving would silently discard the change. To attach a different agent, delete this attachment and create a new one.',
    he: 'שיוך הוא הצמד סוכן-וערוץ, ולכן לא ניתן לשנות אותם כאן — שמירה הייתה מתעלמת מהשינוי בשקט. כדי לשייך סוכן אחר, מחקו את השיוך הזה וצרו חדש.',
  },
  botEntityListColChannelName: { en: 'Channel', he: 'ערוץ' },
  botEntityListColAgentName: { en: 'Agent', he: 'סוכן' },
  channelListNoAgent: { en: 'No agent attached', he: 'לא משויך סוכן' },
  channelListAgentAttached: { en: 'Attached', he: 'משויך' },
  channelListNoAgentHint: {
    en: 'No agent is attached to this channel, so nothing will answer messages that arrive on it. Attach one under Bots → Bot Attachments.',
    he: 'לא משויך סוכן לערוץ הזה, ולכן אף אחד לא יענה להודעות שמגיעות אליו. שייכו סוכן תחת בוטים ← שיוכי בוטים.',
  },
  channelListColActions: { en: 'Actions', he: 'פעולות' },
  channelListUnnamed: { en: '(unnamed)', he: '(ללא שם)' },
  channelListViewSnippet: { en: 'View snippet', he: 'הצג קטע קוד' },

  // ═══════════════════════════════════════════════════════════════════════
  //  OP-579 — per-channel bot mode ("Consultation Only").
  //
  //  Sits beside "No agent attached" in the list because the two answer the same operator
  //  question — what will actually happen when mail arrives here? — and a channel where the bot
  //  only ever drafts is just as quiet as one with no bot at all if nobody says so.
  // ═══════════════════════════════════════════════════════════════════════
  channelBotModeConsultOnlyBadge: { en: 'Drafts only', he: 'טיוטות בלבד' },
  channelBotModeConsultOnlyBadgeHint: {
    en: 'This channel is in Consultation Only mode: the bot writes a reply for every message but never sends one. Nothing reaches the customer until a person approves, edits or rewrites it in the Agent Console.',
    he: 'הערוץ במצב "התייעצות בלבד": הבוט מנסח תשובה לכל הודעה אך לעולם לא שולח. שום דבר לא מגיע ללקוח עד שאדם מאשר, עורך או כותב מחדש בקונסולת הנציג.',
  },
  channelFormBotModeTitle: { en: 'Who sends the reply', he: 'מי שולח את התשובה' },
  channelFormBotModeIntro: {
    en: 'Email channels only. Choose whether the bot answers your customers itself, or only ever prepares a reply for one of your people to send.',
    he: 'ערוצי דוא״ל בלבד. בחרו אם הבוט משיב ללקוחות בעצמו, או רק מכין תשובה שאחד מאנשיכם ישלח.',
  },
  channelFormBotModeAria: { en: 'Bot mode', he: 'מצב הבוט' },
  channelFormBotModeAutonomousLabel: { en: 'The bot answers (default)', he: 'הבוט משיב (ברירת מחדל)' },
  channelFormBotModeAutonomousHint: {
    en: 'Replies go out to the customer. If email review is switched on for this tenant, they are held for the review window first and then sent.',
    he: 'התשובות נשלחות ללקוח. אם סקירת דוא״ל מופעלת לארגון, הן ממתינות לחלון הסקירה ואז נשלחות.',
  },
  channelFormBotModeConsultOnlyLabel: { en: 'Consultation only — a person always sends', he: 'התייעצות בלבד — אדם תמיד שולח' },
  channelFormBotModeConsultOnlyHint: {
    en: 'The bot still reads the message and writes a full reply, but it can never send it. Every reply waits in the Agent Console until someone sends it as written, edits it, asks for a revision, or writes their own. No timer releases it.',
    he: 'הבוט עדיין קורא את ההודעה ומנסח תשובה מלאה, אך לעולם לא יכול לשלוח אותה. כל תשובה ממתינה בקונסולת הנציג עד שמישהו ישלח אותה כפי שהיא, יערוך אותה, יבקש ניסוח מחדש או יכתוב משלו. שום טיימר לא משחרר אותה.',
  },
  channelFormBotModeConsultOnlyWarning: {
    en: 'Nobody watching the review queue means nobody answered. This mode removes the safety net of an eventual automatic send.',
    he: 'אם אף אחד לא עוקב אחרי תור הסקירה — אף אחד לא ענה. המצב הזה מבטל את רשת הביטחון של שליחה אוטומטית בסופו של דבר.',
  },
  channelFormBotModeSave: { en: 'Save bot mode', he: 'שמירת מצב הבוט' },
  channelFormBotModeSaved: { en: 'Saved', he: 'נשמר' },
  channelFormBotModeError: { en: 'Could not save the bot mode', he: 'שמירת מצב הבוט נכשלה' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Inbound-polling state for an email channel (OP-548 / OP-549).
  //
  //  The setup flow can finish with every screen green and still ingest zero mail, because
  //  inbound polling is armed by a PLATFORM-scoped flag outside the flow. When it is off the
  //  poller warns to Elasticsearch, not to the operator. These strings put that state where the
  //  operator is. Shared by the channel list (compact badge) and the channel view (full panel).
  // ═══════════════════════════════════════════════════════════════════════
  channelListColInbound: { en: 'Inbound', he: 'קליטה' },
  channelInboundTitle: { en: 'Inbound polling', he: 'קליטת דואר נכנס' },
  channelInboundReceiving: { en: 'Receiving mail', he: 'קולט דואר' },
  channelInboundPollerReached: { en: 'Polling, no mail yet', he: 'סורק, אין דואר עדיין' },
  channelInboundNoSignal: { en: 'No mail received yet', he: 'לא התקבל דואר עדיין' },
  channelInboundMailboxLabel: { en: 'Mailbox', he: 'תיבת דואר' },
  channelInboundTransportLabel: { en: 'Transport', he: 'ערוץ תעבורה' },
  channelInboundTransportGraph: { en: 'Microsoft Graph', he: 'Microsoft Graph' },
  channelInboundTransportImapSmtp: { en: 'IMAP + SMTP', he: 'IMAP + SMTP' },
  channelInboundLastIngestLabel: { en: 'Last message received', he: 'ההודעה האחרונה שהתקבלה' },
  channelInboundLastIngestNever: { en: 'never', he: 'מעולם לא' },
  channelInboundBaselineLabel: { en: 'Poller baseline', he: 'נקודת התחלה של הסורק' },
  channelInboundBaselineEstablished: { en: 'established', he: 'נקבעה' },
  channelInboundBaselineNotEstablished: { en: 'not established', he: 'לא נקבעה' },
  channelInboundLastCheckLabel: { en: 'Last connection check', he: 'בדיקת החיבור האחרונה' },
  channelInboundPollerReachedHint: {
    en: 'The poller has reached this mailbox — its read position is set — but no customer mail has been ingested yet.',
    he: 'הסורק הגיע לתיבה הזו — נקודת הקריאה שלו נקבעה — אך עדיין לא נקלט דואר מלקוחות.',
  },
  channelInboundNoSignalHint: {
    en: 'Nothing has arrived from this mailbox yet, so inbound polling may not be switched on. It is enabled per platform, not per channel, and this portal cannot read that setting — ask a platform administrator to confirm Email.Inbound.Graph.Enabled (Microsoft Graph) or Email.Inbound.Imap.Enabled (IMAP) is true.',
    he: 'עדיין לא הגיע דבר מהתיבה הזו, ולכן ייתכן שקליטת הדואר הנכנס אינה מופעלת. ההפעלה היא ברמת הפלטפורמה ולא ברמת הערוץ, והפורטל אינו יכול לקרוא את ההגדרה הזו — בקשו ממנהל פלטפורמה לאשר ש-Email.Inbound.Graph.Enabled (Microsoft Graph) או Email.Inbound.Imap.Enabled (IMAP) מוגדר כ-true.',
  },
  channelInboundTestCaveat: {
    en: 'Test Connection proves the credential, not the poller.',
    he: 'בדיקת החיבור מוכיחה את פרטי ההזדהות, לא את הסורק.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Blocked senders on an email channel (channel view).
  //
  //  Staff block a sender from the agent console's spam action; this section only lists and lifts
  //  the blocks. `{address}` is substituted by the component, which renders it inside <bdi> so a
  //  Latin e-mail address embedded in a Hebrew sentence keeps its punctuation on the right side.
  // ═══════════════════════════════════════════════════════════════════════
  channelBlockedSendersTitle: { en: 'Blocked senders', he: 'שולחים חסומים' },
  channelBlockedSendersIntro: {
    en: 'Email from these addresses is not read on this channel.',
    he: 'דוא״ל מהכתובות האלה אינו נקרא בערוץ הזה.',
  },
  channelBlockedSendersColAddress: { en: 'Address', he: 'כתובת' },
  channelBlockedSendersColBlockedAt: { en: 'Blocked on', he: 'נחסם בתאריך' },
  channelBlockedSendersColBlockedBy: { en: 'Blocked by', he: 'נחסם על ידי' },
  channelBlockedSendersColActions: { en: 'Actions', he: 'פעולות' },
  channelBlockedSendersRemove: { en: 'Remove', he: 'הסר' },
  channelBlockedSendersRemoveAria: { en: 'Remove the block on {address}', he: 'הסרת החסימה על {address}' },
  channelBlockedSendersConfirmTitle: { en: 'Remove this block?', he: 'להסיר את החסימה?' },
  channelBlockedSendersConfirmBody: {
    en: 'Remove the block on {address}? From the next mailbox poll, email from this sender will be read again.',
    he: 'להסיר את החסימה על {address}? החל מהסריקה הבאה של תיבת הדואר, דוא״ל מהשולח הזה ייקרא שוב.',
  },
  channelBlockedSendersConfirmAction: { en: 'Remove block', he: 'הסר חסימה' },
  channelBlockedSendersEmpty: { en: 'No sender is blocked on this channel.', he: 'אף שולח אינו חסום בערוץ הזה.' },
  channelBlockedSendersEmptyHint: {
    en: 'Senders are blocked from the Agent Console, using the spam action on an email conversation.',
    he: 'חוסמים שולחים מקונסולת הנציג, באמצעות פעולת הספאם בשיחת דוא״ל.',
  },
  channelBlockedSendersRemoveFailed: {
    en: 'Could not remove the block on {address}. Try again.',
    he: 'לא ניתן היה להסיר את החסימה על {address}. נסו שוב.',
  },
  channelBlockedSendersRemoveGone: {
    en: 'The block on {address} no longer exists. Reload the page to see the current list.',
    he: 'החסימה על {address} כבר אינה קיימת. רעננו את הדף כדי לראות את הרשימה העדכנית.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Channels — create form + embed snippet block
  // ═══════════════════════════════════════════════════════════════════════
  // OP-550 — the post-create screen doubles as the channel VIEW, so the confirmation heading is
  // now conditional; an existing channel shows its own name instead.
  channelFormCreatedHeading: { en: 'Channel Created Successfully', he: 'הערוץ נוצר בהצלחה' },
  channelFormCopied: { en: 'Copied!', he: 'הועתק!' },
  channelFormCopyToClipboard: { en: 'Copy to Clipboard', he: 'העתק ללוח' },
  channelFormCopySecret: { en: 'Copy Secret', he: 'העתק סוד' },
  channelFormCopyExample: { en: 'Copy Example', he: 'העתק דוגמה' },
  channelFormSecretWarning: {
    en: 'Channel Secret — Store server-side now — it will NOT be shown again',
    he: 'סוד ערוץ — אחסן בצד השרת כעת — הוא לא יוצג שוב',
  },
  channelFormEmbedSnippet: { en: 'Embed Snippet', he: 'קטע קוד להטמעה' },
  channelFormEmbedIntro: {
    en: 'Paste this into your website\'s HTML to embed the chat widget:',
    he: 'הדבק זאת ב-HTML של האתר שלך כדי להטמיע את ווידג׳ט הצ׳אט:',
  },
  channelFormIntegrationGuide: {
    en: '📖 Step-by-step installation guide (send this link to the customer)',
    he: '📖 מדריך התקנה שלב-אחר-שלב (שלח קישור זה ללקוח)',
  },
  channelFormHmacExampleTitle: { en: 'Server-side HMAC example (Node.js)', he: 'דוגמת HMAC בצד השרת (Node.js)' },
  // The channel-form "Development: open the chat widgets directly" block was removed with
  // chat-widget-2 (ADR-022) — it linked out to both widgets and made no sense with one left.
  // Its four labels (channelFormDevTitle/DevOpen/DevWidget1/DevWidget2) went with it, 2026-08-09.
  channelFormCreateFailed: { en: 'Failed to create channel.', he: 'יצירת הערוץ נכשלה.' },
  channelFormCreatedTitle: { en: 'Channel Created Successfully', he: 'הערוץ נוצר בהצלחה' },
  channelFormName: { en: 'Name:', he: 'שם:' },
  channelFormType: { en: 'Type:', he: 'סוג:' },
  channelFormWidgetId: { en: 'Widget ID:', he: 'מזהה ווידג׳ט:' },
  channelFormWebhookPath: { en: 'Webhook Path:', he: 'נתיב Webhook:' },
  channelFormIdentityModeLabel: { en: 'Identity Mode:', he: 'מצב זיהוי:' },
  channelFormHmacEnabled: { en: 'Identity verification (HMAC) is enabled.', he: 'אימות זהות (HMAC) מופעל.' },
  channelFormBackToChannels: { en: 'Back to Channels', he: 'חזרה לערוצים' },
  channelFormNewChannel: { en: 'New Channel', he: 'ערוץ חדש' },
  channelFormChannelNameLabel: { en: 'Channel Name *', he: 'שם הערוץ *' },
  channelFormChannelNamePlaceholder: {
    en: 'e.g., Website Chat EN, WhatsApp Support',
    he: 'לדוגמה, צ׳אט אתר EN, תמיכת WhatsApp',
  },
  channelFormChannelNameHint: { en: 'A human-readable name to identify this channel.', he: 'שם קריא לאדם לזיהוי ערוץ זה.' },
  channelFormChannelTypeLabel: { en: 'Channel Type *', he: 'סוג ערוץ *' },
  channelFormTypeWebChat: {
    en: 'WebChat (embedded chat widget via SignalR)',
    he: 'WebChat (ווידג׳ט צ׳אט מוטמע דרך SignalR)',
  },
  channelFormTypeWhatsApp: { en: 'WhatsApp Business', he: 'WhatsApp Business' },
  channelFormTypeEmail: { en: 'Email', he: 'אימייל' },
  channelFormTypeSms: { en: 'SMS', he: 'SMS' },
  channelFormWebchatInfoBold: {
    en: 'WebChat is built in',
    he: 'WebChat מובנה במערכת',
  },
  channelFormWebchatInfoRest: {
    en: ' — no webhook URL or external credentials needed.',
    he: ' — אין צורך בכתובת Webhook או בהרשאות חיצוניות.',
  },
  channelFormWebchatWidgetPre: { en: 'A unique', he: 'לאחר היצירה ייווצר אוטומטית' },
  channelFormWebchatWidgetBold: { en: 'Widget ID', he: 'מזהה ווידג׳ט' },
  channelFormWebchatWidgetPost: { en: ' will be auto-generated after creation.', he: ' ייחודי.' },
  channelFormAllowedOriginsLabel: { en: 'Allowed Origins (optional)', he: 'מקורות מורשים (אופציונלי)' },
  channelFormAllowedOriginsPlaceholder: {
    en: 'https://example.com, https://app.example.com',
    he: 'https://example.com, https://app.example.com',
  },
  channelFormAllowedOriginsHint: {
    en: 'Comma-separated list of domains allowed to embed this widget.',
    he: 'רשימת דומיינים מופרדת בפסיקים שמורשים להטמיע ווידג׳ט זה.',
  },
  channelFormIdentityLabel: { en: 'How customers are identified', he: 'כיצד לקוחות מזוהים' },
  channelFormIdentityAria: { en: 'Identity Mode', he: 'מצב זיהוי' },
  channelFormIdentityAnonymousLabel: { en: 'Anonymous', he: 'אנונימי' },
  channelFormIdentityAnonymousHint: {
    en: 'No identity — customers chat without logging in. Use for public-facing widgets.',
    he: 'ללא זהות — לקוחות מתכתבים ללא התחברות. מתאים לווידג׳טים פומביים.',
  },
  channelFormIdentityEmailLabel: { en: 'Email', he: 'אימייל' },
  channelFormIdentityEmailHint: {
    en: 'Identify customers by their email address. Normalized: trim + lowercase.',
    he: 'זהה לקוחות לפי כתובת האימייל שלהם. מנורמל: חיתוך רווחים + אותיות קטנות.',
  },
  channelFormIdentityPhoneLabel: { en: 'Phone', he: 'טלפון' },
  channelFormIdentityPhoneHint: {
    en: 'Identify customers by their phone number. Normalized: digits + optional leading +.',
    he: 'זהה לקוחות לפי מספר הטלפון שלהם. מנורמל: ספרות + סימן + מוביל אופציונלי.',
  },
  channelFormIdentityExternalIdLabel: { en: 'External ID', he: 'מזהה חיצוני' },
  channelFormIdentityExternalIdHint: {
    en: 'Identify customers by an arbitrary ID from your own system (exact value, no normalization).',
    he: 'זהה לקוחות לפי מזהה שרירותי מהמערכת שלך (ערך מדויק, ללא נרמול).',
  },
  // companyId (M3, CRM v2 WS-4.6). The label says "scope" and the hint spends its whole length on the
  // two consequences, because this is the one option an admin can select for a sensible-sounding
  // reason and get a result they did not intend. Both are stated outright: it identifies nobody on its
  // own, and it splits an existing customer's history.
  channelFormIdentityCompanyIdLabel: { en: 'Company (scope)', he: 'חברה (היקף)' },
  channelFormIdentityCompanyIdHint: {
    en:
      'A company SCOPE, not an identity — a company is not a customer. Sent on its own it identifies ' +
      'nobody and the server treats the session as anonymous, so pair it with Email, Phone or ' +
      'External ID. With it on, the same person at two different companies becomes TWO customers with ' +
      'two separate histories. That is the point — it stops one company seeing another company\'s ' +
      'conversations — but an existing customer\'s history will not follow them.',
    he:
      'היקף של חברה, לא זהות — חברה איננה לקוח. כשנשלח לבד הוא אינו מזהה אף אחד והשרת מתייחס לשיחה ' +
      'כאנונימית, ולכן יש לשלב אותו עם אימייל, טלפון או מזהה חיצוני. כשהאפשרות פעילה, אותו אדם בשתי ' +
      'חברות שונות הופך לשני לקוחות עם שתי היסטוריות נפרדות. זו המטרה — כך חברה אחת אינה רואה את ' +
      'השיחות של חברה אחרת — אך ההיסטוריה של לקוח קיים לא תעבור איתו.',
  },
  // Non-blocking on purpose: the server ACCEPTS this configuration, and the form must not invent a
  // rule the backend does not enforce. It only has to make the silent consequence visible.
  channelFormIdentityCompanyIdOnlyWarning: {
    en:
      'Company is the only parameter selected. A company id identifies nobody on its own, so every ' +
      'session on this channel will be anonymous. Add Email, Phone or External ID.',
    he:
      'חברה היא הפרמטר היחיד שנבחר. מזהה חברה אינו מזהה אף אחד בעצמו, ולכן כל שיחה בערוץ הזה תהיה ' +
      'אנונימית. הוסף אימייל, טלפון או מזהה חיצוני.',
  },
  channelFormRequireVerification: { en: 'Require identity verification', he: 'דרוש אימות זהות' },
  channelFormRecommended: { en: 'RECOMMENDED', he: 'מומלץ' },
  channelFormVerificationHint: {
    en: 'Strongly recommended — without it, a visitor\'s claimed identity is simply taken at face value. Enable to require a secure signed code with each identity claim.',
    he: 'מומלץ מאוד — בלעדיו, זהות שמצהיר עליה מבקר מתקבלת כפי שהיא. הפעל כדי לדרוש קוד חתום מאובטח עם כל הצהרת זהות.',
  },
  channelFormWhatsAppLabel: { en: 'Phone Number ID (Meta) *', he: 'מזהה מספר טלפון (Meta) *' },
  channelFormWhatsAppPlaceholder: { en: 'e.g., 123456789012345', he: 'לדוגמה, 123456789012345' },
  channelFormWhatsAppHint: {
    en: 'The phone_number_id from Meta WhatsApp Business API.',
    he: 'ה-phone_number_id מ-Meta WhatsApp Business API.',
  },
  channelFormSmsLabel: { en: 'Your SMS sending number (from your SMS provider)', he: 'מספר השליחה שלך ב-SMS (מספק ה-SMS שלך)' },
  channelFormSmsPlaceholder: { en: 'e.g., +1234567890 or MG...', he: 'לדוגמה, +1234567890 או MG...' },
  channelFormSmsHint: {
    en: 'The phone number or messaging ID your SMS provider gave you.',
    he: 'מספר הטלפון או מזהה ההודעות שקיבלת מספק ה-SMS שלך.',
  },
  channelFormEmailLabel: { en: 'Inbound Email Address', he: 'כתובת אימייל נכנס' },
  channelFormEmailPlaceholder: { en: 'support@example.com', he: 'support@example.com' },
  channelFormEmailHint: { en: 'Email address that receives customer messages.', he: 'כתובת אימייל המקבלת הודעות מלקוחות.' },
  channelFormCredPathLabel: { en: 'Where credentials are stored *', he: 'היכן ההרשאות מאוחסנות *' },
  channelFormCredPathPlaceholder: { en: 'e.g., channel-key', he: 'לדוגמה, channel-key' },
  channelFormCredPathHint: {
    en: 'A reference to where your credentials are securely stored — never paste the actual key or password here.',
    he: 'הפניה למקום שבו ההרשאות שלך מאוחסנות באופן מאובטח — לעולם אל תדביק כאן את המפתח או הסיסמה בפועל.',
  },
  channelFormWebhookLabel: { en: 'Webhook URL (optional)', he: 'כתובת Webhook (אופציונלי)' },
  channelFormWebhookPlaceholder: { en: 'https://hooks.example.com/...', he: 'https://hooks.example.com/...' },
  channelFormWebhookHint: {
    en: 'Your inbound webhook URL will be auto-generated after creation.',
    he: 'כתובת ה-Webhook הנכנס שלך תיווצר אוטומטית לאחר היצירה.',
  },
  channelFormCreating: { en: 'Creating...', he: 'יוצר...' },
  channelFormCreateChannel: { en: 'Create Channel', he: 'צור ערוץ' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Channels — Email channel fields (feature/email-channel)
  // ═══════════════════════════════════════════════════════════════════════
  channelFormEmailIdentityNoteAria: { en: 'Email identity mode', he: 'מצב זיהוי אימייל' },
  channelFormEmailIdentityNoteTitle: { en: 'Identity mode: Email (fixed)', he: 'מצב זיהוי: אימייל (קבוע)' },
  channelFormEmailIdentityNoteBody: {
    en: 'Email channels always identify customers by their email address. This is enforced server-side and cannot be changed per-channel.',
    he: 'ערוצי אימייל תמיד מזהים לקוחות לפי כתובת האימייל שלהם. הדבר נאכף בצד השרת ולא ניתן לשינוי לפי ערוץ.',
  },
  channelFormEmailProviderLabel: { en: 'Email Provider *', he: 'ספק אימייל *' },
  channelFormEmailProviderHint: {
    en: 'Mailgun can securely verify incoming mail on its own. SendGrid and Postmark need an extra security check that is not yet available.',
    he: 'Mailgun יכול לאמת דואר נכנס באופן מאובטח בעצמו. SendGrid ו-Postmark זקוקים לבדיקת אבטחה נוספת שאינה זמינה עדיין.',
  },
  channelFormEmailProviderMailgun: { en: 'Mailgun', he: 'Mailgun' },
  channelFormEmailProviderMock: { en: 'Mock (dev / credential-free E2E)', he: 'Mock (פיתוח / E2E ללא הרשאות)' },
  // ADR-021 — the production credential-free case: no provider account at all, inbound by
  // polling the tenant's own mailbox over IMAP. Named for the operator's mental model ('my own
  // mailbox'), not the implementation, and deliberately listed right after Mock so the two
  // credential-free options sit together while only this one reads as production-safe.
  channelFormEmailProviderByoMailbox: {
    en: 'My own mailbox (IMAP + SMTP — no provider account)',
    he: 'תיבת הדואר שלי (IMAP + SMTP — ללא חשבון ספק)',
  },
  channelFormEmailProviderSendgrid: {
    en: 'SendGrid (needs compensating gate — not yet available)',
    he: 'SendGrid (דורש בקרת אבטחה מפצה — עדיין לא זמין)',
  },
  channelFormEmailProviderPostmark: {
    en: 'Postmark (needs compensating gate — not yet available)',
    he: 'Postmark (דורש בקרת אבטחה מפצה — עדיין לא זמין)',
  },
  channelFormEmailInboundLabel: { en: 'Inbound Email Address *', he: 'כתובת אימייל נכנס *' },
  channelFormEmailInboundPlaceholder: { en: 'support@mg.yourcompany.com', he: 'support@mg.yourcompany.com' },
  channelFormEmailInboundHint: {
    en: 'The address customers send to. Configure this as your Mailgun inbound-parse route recipient. Also used as the outbound From address when no separate From address is set.',
    he: 'הכתובת שאליה לקוחות שולחים. הגדר זאת כנמען מסלול ה-inbound-parse של Mailgun. משמש גם ככתובת השולח היוצא כאשר לא הוגדרה כתובת From נפרדת.',
  },
  channelFormEmailFromLabel: { en: 'From Address (optional)', he: 'כתובת שולח (אופציונלי)' },
  channelFormEmailFromPlaceholder: { en: 'support@mg.yourcompany.com', he: 'support@mg.yourcompany.com' },
  channelFormEmailFromHint: {
    en: 'Outbound reply From address. Leave blank to use the inbound address above — the server applies the same fallback automatically.',
    he: 'כתובת השולח לתגובות יוצאות. השאר ריק כדי להשתמש בכתובת הנכנסת שלמעלה — השרת מיישם את אותה ברירת מחדל אוטומטית.',
  },
  channelFormEmailVaultLabel: {
    en: 'Provider API Key / Webhook Signing Key (Vault Reference) *',
    he: 'מפתח API של הספק / מפתח חתימת Webhook (הפניית Vault) *',
  },
  channelFormEmailVaultPlaceholder: { en: 'vault://secrets/mailgun-api-key', he: 'vault://secrets/mailgun-api-key' },
  channelFormEmailVaultHint: {
    en: 'Never paste the raw API key here — store it in Vault and reference the path. For Mailgun this is the API send key + webhook signing key (stored together under one vault path or as separate sub-paths per your Vault layout).',
    he: 'לעולם אל תדביק כאן את מפתח ה-API הגולמי — אחסן אותו ב-Vault והפנה לנתיב. עבור Mailgun זהו מפתח השליחה של ה-API + מפתח חתימת ה-Webhook.',
  },
  channelFormEmailWebhookTitle: { en: 'Mailgun Inbound Webhook Route', he: 'מסלול Webhook נכנס של Mailgun' },
  channelFormEmailWebhookIntro: {
    en: 'Configure this URL as your Mailgun Routes inbound-parse endpoint:',
    he: 'הגדר כתובת זו כנקודת הקצה של inbound-parse ב-Mailgun Routes:',
  },
  channelFormEmailWebhookCopy: { en: 'Copy Webhook URL', he: 'העתק כתובת Webhook' },
  channelFormEmailWebhookSigningNote: {
    en: 'Mailgun signing key: The Mailgun webhook signing key (from your Mailgun account settings) must be stored at the Provider API Key path you specified above. The signing key is used to verify every inbound delivery — it is never stored in plain text.',
    he: 'מפתח חתימה של Mailgun: מפתח חתימת ה-Webhook של Mailgun (מהגדרות חשבון Mailgun שלך) חייב להיות מאוחסן בנתיב מפתח ה-API שציינת למעלה. מפתח החתימה משמש לאימות כל מסירה נכנסת — הוא לעולם לא מאוחסן כטקסט גלוי.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  ADR-021 — Channels — Inbound email (BYO-IMAP), tenant-owned mailbox
  // ═══════════════════════════════════════════════════════════════════════
  channelFormImapTitle: { en: 'Inbound mailbox (your own mailbox)', he: 'תיבת דואר נכנס (תיבת הדואר שלך)' },
  channelFormImapIntro: {
    en: 'Optional. Fill this in to let the bot receive customer email directly from your own support mailbox. Messages are only read — never marked read, moved, or deleted.',
    he: 'אופציונלי. מלא שדות אלה כדי לאפשר לבוט לקבל דואר לקוחות ישירות מתיבת הדואר של התמיכה שלך. הודעות נקראות בלבד — לעולם לא מסומנות כנקראו, לא מועברות ולא נמחקות.',
  },
  channelFormImapHostLabel: { en: 'IMAP host', he: 'שרת IMAP' },
  channelFormImapPortLabel: { en: 'IMAP port', he: 'פורט IMAP' },
  channelFormImapUserLabel: { en: 'IMAP username', he: 'שם משתמש IMAP' },
  channelFormImapPasswordLabel: { en: 'IMAP password', he: 'סיסמת IMAP' },
  channelFormImapFolderLabel: { en: 'Mailbox folder', he: 'תיקיית תיבת הדואר' },
  channelFormImapUseSslLabel: { en: 'Use SSL', he: 'שימוש ב-SSL' },
  channelFormImapGmailHint: {
    en: 'For Gmail, use a Google app password — not your account password. The usual setting is imap.gmail.com, port 993.',
    he: 'עבור Gmail, השתמש בסיסמת אפליקציה של Google — לא בסיסמת החשבון שלך. ההגדרה הרגילה היא imap.gmail.com, פורט 993.',
  },
  channelFormImapValidationMissing: {
    en: 'IMAP username and password are required when a host is set.',
    he: 'שם משתמש וסיסמה של IMAP נדרשים כאשר שרת מוגדר.',
  },
  // Multi-parameter identity (2026-08-04)
  channelFormIdentityMultiHint: {
    en: 'Pick every parameter this channel may use. Authentication tries them in the order you select them, so if the page cannot supply the first one it falls back to the next.',
    he: 'בחר כל פרמטר שהערוץ הזה יכול להשתמש בו. האימות מנסה אותם בסדר שבו בחרת, כך שאם הדף לא יכול לספק את הראשון, הוא עובר לבא אחריו.',
  },
  channelFormIdentityOrderBadge: {
    en: 'Order',
    he: 'סדר',
  },
  channelFormIdentityAnonymousExclusive: {
    en: 'Anonymous means no identity at all, so it cannot be combined with another parameter.',
    he: 'אנונימי פירושו ללא זיהוי כלל, ולכן לא ניתן לשלב אותו עם פרמטר אחר.',
  },
  channelFormIdentityAtLeastOne: {
    en: 'Choose at least one way to identify customers.',
    he: 'בחר לפחות דרך אחת לזיהוי לקוחות.',
  },
  channelFormExternalIdCrmPropertyLabel: {
    en: 'CRM property holding this ID',
    he: 'מאפיין ב-CRM שמכיל מזהה זה',
  },
  channelFormExternalIdCrmPropertyHint: {
    en: 'The property name in your CRM that stores your own customer ID (for example customer_number or member_id). We match the ID the page supplies against this property, which is what lets us confirm it instead of taking it on trust.',
    he: 'שם המאפיין ב-CRM שלך שבו מאוחסן מזהה הלקוח שלך (לדוגמה customer_number או member_id). אנו משווים את המזהה שהדף מספק למאפיין זה, וכך אנו יכולים לאמת אותו במקום לסמוך עליו.',
  },
  channelFormExternalIdCrmPropertyRequired: {
    en: 'A CRM property name is required when External ID is selected — without it an external ID cannot be verified.',
    he: 'נדרש שם מאפיין ב-CRM כאשר נבחר מזהה חיצוני — בלעדיו לא ניתן לאמת מזהה חיצוני.',
  },
  channelFormExternalIdCrmPropertyInvalid: {
    en: 'Use lowercase letters, digits and underscores only, starting with a letter (for example customer_number).',
    he: 'השתמש באותיות קטנות, ספרות וקווים תחתונים בלבד, והתחל באות (לדוגמה customer_number).',
  },
  channelFormImapTestTitle: { en: 'Verify inbound mailbox', he: 'אמת תיבת דואר נכנס' },
  channelFormImapTestButton: { en: 'Test Connection', he: 'בדוק חיבור' },
  channelFormImapTesting: { en: 'Testing...', he: 'בודק...' },
  channelFormImapTestSuccess: { en: 'Connection succeeded.', he: 'החיבור הצליח.' },
  channelFormImapTestFailed: { en: 'Connection failed.', he: 'החיבור נכשל.' },
  // OP-548 — the persisted "last verified / last error / never verified" trio is gone from here:
  // that state now renders once, in the inbound-status panel (`channelInbound*` above), for every
  // transport instead of IMAP only. "Verified" was also the wrong word for it — the same field is
  // stamped by Test Connection, which verifies a credential and says nothing about the poller.
  // ADR-021 A1 — Microsoft 365 app-only OAuth (XOAUTH2).
  channelFormImapAuthModeLabel: { en: 'Sign-in method', he: 'שיטת התחברות' },
  channelFormImapAuthModePassword: {
    en: 'Password / app password (Gmail, Zoho, cPanel…)',
    he: 'סיסמה / סיסמת אפליקציה (Gmail, Zoho, cPanel…)',
  },
  channelFormImapAuthModeOAuth: {
    en: 'Microsoft 365 (OAuth — required for M365)',
    he: 'Microsoft 365 (OAuth — נדרש עבור M365)',
  },
  channelFormImapOAuthTenantIdLabel: { en: 'Directory (tenant) ID', he: 'מזהה ספרייה (Tenant ID)' },
  channelFormImapOAuthClientIdLabel: { en: 'Application (client) ID', he: 'מזהה יישום (Client ID)' },
  channelFormImapOAuthClientSecretLabel: { en: 'Client secret', he: 'סוד יישום (Client Secret)' },
  channelFormImapOAuthHint: {
    en: 'Microsoft switched off password sign-in for mailboxes in 2022, so a Microsoft 365 mailbox must use OAuth. These three values come from the app registration in your Azure portal. Host is usually outlook.office365.com, port 993. Your Microsoft admin also has to grant that app access to this specific mailbox — see the setup guide.',
    he: 'מיקרוסופט ביטלה התחברות עם סיסמה לתיבות דואר ב-2022, ולכן תיבת Microsoft 365 חייבת OAuth. שלושת הערכים מגיעים מרישום היישום בפורטל Azure. השרת הוא בדרך כלל outlook.office365.com, פורט 993. מנהל ה-Microsoft שלך צריך גם להעניק ליישום גישה לתיבה הספציפית הזו — ראה את מדריך ההתקנה.',
  },
  channelFormImapOAuthValidationMissing: {
    en: 'Directory (tenant) ID, Application (client) ID and client secret are all required for Microsoft 365 sign-in.',
    he: 'מזהה ספרייה, מזהה יישום וסוד היישום — כולם נדרשים עבור התחברות Microsoft 365.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Channels — WhatsApp via 360Dialog (ADR-020)
  // ═══════════════════════════════════════════════════════════════════════
  channelFormWaCredsTitle: {
    en: '360Dialog Connection (your WhatsApp Business number)',
    he: 'חיבור 360Dialog (מספר WhatsApp Business שלך)',
  },
  channelFormWaCredsHint: {
    en: 'Each tenant connects its own WhatsApp: paste the D360 API key issued for your phone number at 360Dialog. The key is encrypted at rest and never shown again.',
    he: 'כל ארגון מחבר את ה-WhatsApp שלו: הדביקו את מפתח ה-D360 API שהונפק למספר הטלפון שלכם ב-360Dialog. המפתח מוצפן באחסון ולא יוצג שוב.',
  },
  // ADR-020 Phase 2 — attach this number to a 360Dialog account whose single webhook covers every
  // number on it, or leave it on a webhook of its own. 360Dialog supports both scopes; neither
  // option is a fallback for the other.
  channelFormWaConnectionLabel: { en: 'Receives through', he: 'מקבל דרך' },
  channelFormWaConnectionNone: {
    en: 'Its own webhook (this number only)',
    he: 'Webhook משלו (מספר זה בלבד)',
  },
  channelFormWaConnectionHint: {
    en: 'Pick the 360Dialog account this number belongs to, and it will receive through that account’s shared webhook. Manage accounts under Settings › Channel Configurations › WhatsApp.',
    he: 'בחרו את חשבון 360Dialog שאליו שייך המספר, והוא יקבל הודעות דרך ה-Webhook המשותף של אותו חשבון. ניהול חשבונות: הגדרות › הגדרות ערוצים › ווטסאפ.',
  },
  channelFormWaConnectionEmptyHint: {
    en: 'No 360Dialog accounts have been added yet, so this number will get a webhook of its own — which is fine for a single number. To share one webhook across several numbers, add an account under Settings › Channel Configurations › WhatsApp first.',
    he: 'לא נוספו עדיין חשבונות 360Dialog, ולכן המספר יקבל Webhook משלו — מתאים לחלוטין למספר בודד. כדי לשתף Webhook אחד בין כמה מספרים, הוסיפו קודם חשבון בהגדרות › הגדרות ערוצים › ווטסאפ.',
  },
  channelFormWaConnectionsLoadError: {
    en: 'Could not load your 360Dialog accounts. You can still create this channel with a webhook of its own and attach it later.',
    he: 'לא ניתן היה לטעון את חשבונות 360Dialog שלכם. עדיין ניתן ליצור את הערוץ עם Webhook משלו ולשייך אותו מאוחר יותר.',
  },
  channelFormWaApiKey: { en: 'D360 API Key', he: 'מפתח D360 API' },
  channelFormWaPhoneNumber: { en: 'WhatsApp phone number (E.164)', he: 'מספר טלפון WhatsApp (E.164)' },
  channelFormWaWabaId: { en: 'WABA ID (optional)', he: 'מזהה WABA (אופציונלי)' },
  // OP-516 — the inbound webhook URL is DERIVED, never entered. This replaced a free-text
  // "Webhook URL (optional)" input whose value no code path ever read.
  channelFormInboundWebhookLabel: { en: 'Inbound webhook URL', he: 'כתובת Webhook נכנס' },
  channelFormInboundWebhookPending: {
    en: 'Generated automatically when you create the channel',
    he: 'נוצרת אוטומטית בעת יצירת הערוץ',
  },
  channelFormInboundWebhookHint: {
    en: 'We build this URL and register it at the provider for you when you press Provision. Outbound messages are not a webhook — we call the provider’s API directly — so there is nothing else to configure here.',
    he: 'אנחנו בונים את הכתובת ורושמים אותה אצל הספק בלחיצה על Provision. הודעות יוצאות אינן Webhook — אנחנו קוראים ל-API של הספק ישירות — ולכן אין כאן מה להגדיר.',
  },
  // OP-481 — both phone labels already promised "(E.164)" while nothing enforced it, so a local
  // number like 0501234567 was accepted here and rejected opaquely by Meta.
  channelFormPhoneE164Invalid: {
    en: 'Enter the number in international E.164 format — a leading + and country code, e.g. +972501234567.',
    he: 'יש להזין את המספר בפורמט E.164 בינלאומי — + וקידומת מדינה, למשל ‎+972501234567.',
  },
  channelFormWaValidationMissing: {
    en: 'D360 API key and phone number are required together.',
    he: 'מפתח D360 API ומספר טלפון נדרשים יחד.',
  },
  // OP-626 — shown next to "Create Channel" on the WhatsApp create form. Saving the channel
  // does NOT register anything at 360Dialog: the webhook push is a separate, explicit Provision
  // press (per-number) or a manual Hub registration (connection/WABA). An unregistered webhook
  // produces no error anywhere, so without this notice a brand-new channel looks finished and
  // silently receives nothing.
  channelFormWaCreateNoticeTitle: {
    en: 'Saving does not connect this number yet',
    he: 'השמירה עדיין לא מחברת את המספר',
  },
  channelFormWaCreateNoticeBody: {
    en: 'After you create the channel, press Provision on the next screen to register its webhook at 360Dialog. Until you do, no incoming WhatsApp messages reach the bot — and nothing reports an error.',
    he: 'לאחר יצירת הערוץ, לחצו על Provision במסך הבא כדי לרשום את ה-webhook ב-360Dialog. עד אז, הודעות WhatsApp נכנסות לא יגיעו לבוט — ולא תוצג שום שגיאה.',
  },
  channelFormWaCreateNoticeConnectionBody: {
    en: 'This number receives through a shared connection, so there is no Provision button for it. Register the connection\'s webhook URL and secret header in the 360Dialog Hub (Settings → WhatsApp connections). Until you do, no incoming WhatsApp messages reach the bot — and nothing reports an error.',
    he: 'המספר מקבל דרך חיבור משותף, ולכן אין עבורו כפתור Provision. רשמו את כתובת ה-webhook ואת כותרת הסוד של החיבור ב-360Dialog Hub (הגדרות → חיבורי WhatsApp). עד אז, הודעות WhatsApp נכנסות לא יגיעו לבוט — ולא תוצג שום שגיאה.',
  },
  // OP-166 — the channel edit page. Narrow on purpose; the scope note says so on screen.
  channelEditTitle: { en: 'Edit channel', he: 'עריכת ערוץ' },
  channelEditLoading: { en: 'Loading channel...', he: 'טוען ערוץ...' },
  channelEditNotFound: { en: 'Channel not found.', he: 'הערוץ לא נמצא.' },
  channelEditScopeNote: {
    en: 'This edits the channel\'s name, its provider identifier, whether it is enabled, and — for a chat-widget channel — its approved domains, session lifetime and identity settings. Credentials and email settings are not editable here: each has its own screen.',
    he: 'כאן ניתן לערוך את שם הערוץ, את מזהה הספק, את מצב ההפעלה, ובערוץ צ׳אט — גם דומיינים מאושרים, משך חיי סשן והגדרות זיהוי. פרטי התחברות והגדרות דוא"ל אינם ניתנים לעריכה כאן — לכל אחד מסך משלו.',
  },
  channelEditNameLabel: { en: 'Channel name', he: 'שם הערוץ' },
  channelEditExternalIdLabel: { en: 'External channel identifier', he: 'מזהה ערוץ חיצוני' },
  channelEditExternalIdHint: {
    en: 'The provider-side identifier this channel answers for.',
    he: 'המזהה בצד הספק שעבורו הערוץ עונה.',
  },
  channelEditPhoneNumberIdLabel: { en: 'phone_number_id (Meta)', he: 'phone_number_id (Meta)' },
  channelEditPhoneNumberIdHint: {
    en: 'Meta\'s id for this WhatsApp number — NOT the phone number and NOT the WABA ID, which is the same shape and is the usual mix-up. Find it in the 360Dialog Hub, or in Meta Business Manager under the number as "Phone number ID".',
    he: 'המזהה של Meta למספר ווטסאפ זה — לא מספר הטלפון ולא מזהה ה-WABA, שנראה זהה וזו הטעות הנפוצה. ניתן למצוא אותו ב-360Dialog Hub או ב-Meta Business Manager תחת המספר בשם "Phone number ID".',
  },
  channelEditPhoneNumberIdInvalid: {
    en: 'Expected a numeric id (digits only). Note a valid-looking id can still be the wrong one — a WABA ID is digits too.',
    he: 'נדרש מזהה מספרי (ספרות בלבד). שימו לב שמזהה תקין למראה עדיין עשוי להיות השגוי — גם מזהה WABA מורכב מספרות.',
  },
  channelEditPhoneNumberIdChangeWarning: {
    en: 'You are changing which WhatsApp number this channel answers for. Nothing is registered at 360Dialog by this change — the webhook URL and secret belong to the channel, not the number, so they stay as they are and no re-provision is needed. Inbound messages start matching on the next delivery.',
    he: 'אתם משנים לאיזה מספר ווטסאפ הערוץ עונה. השינוי אינו רושם דבר ב-360Dialog — כתובת ה-webhook והסוד שייכים לערוץ ולא למספר, ולכן הם נשארים ואין צורך בהקצאה מחדש. הודעות נכנסות יתאימו החל מהמשלוח הבא.',
  },
  channelEditEnabledLabel: { en: 'Channel is enabled', he: 'הערוץ פעיל' },

  // ── Suggested replies (fast continuation) — WebChat only for now; create + edit ──
  channelSuggestedRepliesLabel: { en: 'Suggested replies', he: 'תשובות מוצעות' },
  channelSuggestedRepliesHint: {
    en: 'After each bot answer, show the customer two short replies they can tap to send.',
    he: 'אחרי כל תשובה של הבוט, הלקוח יראה שתי תשובות קצרות שאפשר ללחוץ עליהן כדי לשלוח.',
  },

  // ── WebChat settings that were create-only until now ────────────────────
  channelEditWebChatSectionTitle: { en: 'Chat widget settings', he: 'הגדרות וידג׳ט הצ׳אט' },
  channelEditDomainsLabel: { en: 'Approved domains', he: 'דומיינים מאושרים' },
  channelEditDomainsHint: {
    en: 'Comma-separated. Only these sites may open a chat on this channel — a copied snippet fails anywhere else. Full origins, including https://, with no path.',
    he: 'מופרדים בפסיקים. רק אתרים אלו רשאים לפתוח צ׳אט בערוץ זה. הזינו מקור מלא כולל https:// וללא נתיב.',
  },
  channelEditDomainsRequired: {
    en: 'Add at least one approved domain. An empty list stops the widget opening at all, and switches off the check that keeps your snippet from working on somebody else’s site. To take the channel offline, clear the “Channel is enabled” box instead.',
    he: 'הוסיפו לפחות דומיין מאושר אחד. רשימה ריקה מונעת מהווידג׳ט להיפתח בכלל.',
  },
  channelEditDomainsInvalid: {
    en: 'Each entry must be a full site origin such as https://www.example.com — no wildcards, no paths.',
    he: 'כל רשומה חייבת להיות מקור מלא, לדוגמה https://www.example.com — ללא תווים כלליים וללא נתיב.',
  },
  channelEditLifetimeLabel: { en: 'Session lifetime (minutes)', he: 'משך חיי הסשן (דקות)' },
  channelEditLifetimeHint: {
    en: 'How long a visitor stays signed in to chat before the widget renews. Maximum 1440 (24 hours): a stolen credential cannot be renewed, but it stays usable for whatever is left of this.',
    he: 'משך הזמן שבו מבקר נשאר מחובר לצ׳אט לפני חידוש. מקסימום 1440 (24 שעות).',
  },
  channelEditLifetimeInvalid: {
    en: 'Enter a whole number of minutes between 1 and 1440.',
    he: 'הזינו מספר שלם של דקות בין 1 ל-1440.',
  },
  channelEditIdentityLabel: { en: 'How customers are identified', he: 'כיצד לקוחות מזוהים' },
  channelEditVerificationLabel: { en: 'Verify customer identity with a signature (HMAC)', he: 'אימות זהות לקוח באמצעות חתימה (HMAC)' },
  channelEditVerificationTurnOnWarning: {
    en: 'Turning this on issues a NEW channel secret, shown once. Any site already signing with the previous secret stops being recognised the moment you save.',
    he: 'הפעלה מנפיקה סוד ערוץ חדש שיוצג פעם אחת בלבד. כל אתר שחותם בסוד הקודם יפסיק להיות מזוהה.',
  },
  channelEditSave: { en: 'Save changes', he: 'שמור שינויים' },
  channelEditSaving: { en: 'Saving...', he: 'שומר...' },
  channelEditNoChanges: { en: 'No changes to save', he: 'אין שינויים לשמירה' },
  channelEditSaved: { en: 'Saved.', he: 'נשמר.' },
  channelEditSaveFailed: { en: 'Could not save the channel.', he: 'לא ניתן היה לשמור את הערוץ.' },
  channelEditBackToChannel: { en: 'Back to channel', he: 'חזרה לערוץ' },
  channelEditBackToChannels: { en: 'Back to channels', he: 'חזרה לערוצים' },
  channelEditOpen: { en: 'Edit channel', he: 'ערוך ערוץ' },

  channelFormWaProvisionTitle: { en: 'Connect to 360Dialog', he: 'חיבור ל-360Dialog' },
  channelFormWaProvisionHint: {
    en: 'Provision registers this channel\'s webhook at 360Dialog so inbound WhatsApp messages reach the bot. Run it once now, and again after rotating the API key.',
    he: 'החיבור רושם את ה-webhook של הערוץ ב-360Dialog כך שהודעות WhatsApp נכנסות יגיעו לבוט. הריצו פעם אחת עכשיו, ושוב לאחר החלפת מפתח ה-API.',
  },
  channelFormWaProvisionButton: { en: 'Provision', he: 'חבר' },
  channelFormWaProvisioning: { en: 'Provisioning...', he: 'מחבר...' },
  channelFormWaProvisionSuccess: { en: 'Webhook registered at 360Dialog.', he: 'ה-webhook נרשם ב-360Dialog.' },
  channelFormWaProvisionFailed: { en: 'Provisioning failed.', he: 'החיבור נכשל.' },
  channelFormWaStatusButton: { en: 'Check status', he: 'בדוק סטטוס' },
  channelFormWaStatusChecking: { en: 'Checking...', he: 'בודק...' },
  channelFormWaStatusMatch: { en: 'Webhook registered and matching.', he: 'ה-webhook רשום ותואם.' },
  // OP-633/OP-634 — a registered URL is only half of "wired up". Without our secret header every
  // inbound delivery is rejected 401 and the channel looks silent rather than broken.
  channelFormWaSecretHeaderOk: {
    en: 'Secret header registered — inbound deliveries can authenticate.',
    he: 'כותרת הסוד רשומה — משלוחים נכנסים יכולים לעבור אימות.',
  },
  channelFormWaSecretHeaderMissing: {
    en: '360Dialog is NOT sending our secret header, so every incoming message is rejected and the bot never sees it. Add a custom header with the name and value below to this number\'s webhook in the 360Dialog Hub, or press Provision again to get a fresh value.',
    he: '‏360Dialog אינו שולח את כותרת הסוד שלנו, ולכן כל הודעה נכנסת נדחית והבוט אינו רואה אותה. הוסיפו כותרת מותאמת עם השם והערך שלמטה ל-webhook של המספר ב-360Dialog Hub, או לחצו Provision שוב לקבלת ערך חדש.',
  },
  channelFormWaSecretHeaderNameLabel: { en: 'Header name', he: 'שם הכותרת' },
  channelFormWaProvisionSecretTitle: {
    en: 'Webhook secret — shown once. Copy it now if you need to register the header by hand.',
    he: 'סוד ה-Webhook — מוצג פעם אחת. העתיקו אותו כעת אם תצטרכו לרשום את הכותרת ידנית.',
  },
  channelFormWaStatusMismatch: {
    en: 'Registered webhook does not match this channel — re-provision.',
    he: 'ה-webhook הרשום אינו תואם לערוץ זה — יש לחבר מחדש.',
  },
  channelFormWaStatusNoCreds: {
    en: 'No 360Dialog credentials configured yet.',
    he: 'טרם הוגדרו פרטי 360Dialog.',
  },
  channelFormWaTestTitle: { en: 'Send a test WhatsApp message', he: 'שליחת הודעת WhatsApp לבדיקה' },
  channelFormWaTestRecipient: { en: 'Recipient phone (E.164)', he: 'טלפון נמען (E.164)' },
  channelFormWaTestButton: { en: 'Send test message', he: 'שלח הודעת בדיקה' },
  channelFormWaTestSending: { en: 'Sending...', he: 'שולח...' },
  channelFormWaTestSuccess: { en: 'Test message sent — check the phone.', he: 'הודעת הבדיקה נשלחה — בדקו בטלפון.' },
  channelFormWaTestFailed: { en: 'Failed:', he: 'נכשל:' },
  channelFormWaTestWindowHint: {
    en: 'Note: Meta only allows free-form messages within 24h of the recipient\'s last message to this number. Message the business number from the test phone first.',
    he: 'שימו לב: Meta מתירה הודעות חופשיות רק בתוך 24 שעות מההודעה האחרונה של הנמען למספר זה. שלחו קודם הודעה למספר העסקי מטלפון הבדיקה.',
  },
  channelFormWaNotConfiguredHint: {
    en: 'Add your 360Dialog API key and phone number (edit this channel) to enable provisioning.',
    he: 'הוסיפו מפתח 360Dialog API ומספר טלפון (ערכו ערוץ זה) כדי לאפשר חיבור.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Channels — Email deferral overrides (per-tenant config)
  // ═══════════════════════════════════════════════════════════════════════
  channelFormDeferralTitle: {
    en: 'Deferral Overrides (optional — applies to your tenant)',
    he: 'דריסות דחייה (אופציונלי — חל על הארגון שלך)',
  },

  // ADR-021 A4 - which Microsoft transport a Microsoft 365 (OAuth) mailbox uses.
  channelFormEmailTransportLabel: {
    en: 'How should we connect to the mailbox?',
    he: 'איך נתחבר לתיבת הדואר?',
  },
  channelFormEmailTransportGraph: {
    en: 'Microsoft Graph (recommended)',
    he: 'Microsoft Graph (מומלץ)',
  },
  channelFormEmailTransportImapSmtp: {
    en: 'IMAP + SMTP (legacy)',
    he: 'IMAP + SMTP (מיושן)',
  },
  channelFormEmailTransportGraphHint: {
    en: 'Reads and sends over the Microsoft Graph API. Works on a standard Microsoft 365 tenant with no extra setup.',
    he: 'קורא ושולח דרך Microsoft Graph API. עובד בארגון Microsoft 365 רגיל ללא הגדרות נוספות.',
  },
  channelFormEmailTransportImapSmtpHint: {
    en: 'Requires Authenticated SMTP to be enabled for this mailbox. Microsoft 365 disables it by default, so sending will fail until an administrator turns it on.',
    he: 'דורש הפעלת SMTP מאומת עבור תיבת דואר זו. Microsoft 365 מכבה אותו כברירת מחדל, ולכן שליחה תיכשל עד שמנהל מערכת יפעיל אותו.',
  },

  // ADR-021 A2 - delegated consent ("Connect") vs app-only (advanced).
  channelFormImapOAuthPathLabel: { en: 'How should we sign in to Microsoft?', he: 'איך נתחבר ל-Microsoft?' },
  channelFormImapOAuthPathDelegated: {
    en: 'Connect with Microsoft (recommended - one click)',
    he: 'התחברות עם Microsoft (מומלץ - בלחיצה אחת)',
  },
  channelFormImapOAuthPathAppOnly: {
    en: 'Advanced: use our own app registration',
    he: 'מתקדם: שימוש ברישום יישום שלנו',
  },
  channelFormImapConnectButton: { en: 'Connect Microsoft mailbox', he: 'חבר תיבת דואר של Microsoft' },
  channelFormImapConnecting: { en: 'Opening Microsoft sign-in...', he: 'פותח התחברות ל-Microsoft...' },
  channelFormImapConnectHint: {
    en: 'You will be sent to Microsoft to sign in as this support mailbox and approve access once. Nothing else is needed - no app registration, no PowerShell.',
    he: 'תועבר ל-Microsoft כדי להתחבר כתיבת הדואר הזו ולאשר גישה פעם אחת. לא נדרש דבר נוסף - בלי רישום יישום ובלי PowerShell.',
  },
  channelFormImapConnectAfterSave: {
    en: 'Save the channel first, then connect the mailbox.',
    he: 'שמור את הערוץ קודם, ולאחר מכן חבר את תיבת הדואר.',
  },
  channelFormImapConnectedAs: { en: 'Connected as', he: 'מחובר בשם' },
  channelFormImapNotConnected: { en: 'Not connected yet.', he: 'לא מחובר עדיין.' },

  // Unified Microsoft 365 OAuth (one login for send & receive) — top-level connection-method
  // choice for byoMailbox, and the minimal one-field card that replaces the old two-card setup.
  channelFormConnectionMethodLabel: { en: 'Connection method', he: 'שיטת חיבור' },
  channelFormConnectionMethodPasswordLabel: {
    en: 'Password + relay',
    he: 'סיסמה + ממסר',
  },
  channelFormConnectionMethodPasswordHint: {
    en: 'Enter separate SMTP and IMAP credentials below — works with Gmail, Zoho, cPanel, or an SMTP relay like SMTP2Go.',
    he: 'הזינו פרטי SMTP ו-IMAP בנפרד למטה — עובד עם Gmail, Zoho, cPanel, או ממסר SMTP כמו SMTP2Go.',
  },
  channelFormConnectionMethodOauthLabel: {
    en: 'Microsoft 365 (OAuth — one login for sending & receiving)',
    he: 'Microsoft 365 (OAuth — התחברות אחת לשליחה ולקבלה)',
  },
  channelFormConnectionMethodOauthHint: {
    en: 'One Microsoft sign-in authorizes both directions — no password, no separate SMTP setup.',
    he: 'התחברות אחת ל-Microsoft מאשרת את שני הכיוונים — בלי סיסמה, בלי הגדרת SMTP נפרדת.',
  },
  channelFormOauthCardTitle: { en: 'Connect your Microsoft 365 mailbox', he: 'חברו את תיבת הדואר של Microsoft 365' },
  channelFormOauthCardHint: {
    en: 'Enter the mailbox address and connect — this one login authorizes both receiving and sending through this mailbox.',
    he: 'הזינו את כתובת תיבת הדואר והתחברו — התחברות אחת זו מאשרת גם קבלה וגם שליחה דרך תיבת דואר זו.',
  },
  channelFormOauthMailboxLabel: { en: 'Mailbox address', he: 'כתובת תיבת הדואר' },
  channelFormOauthAdvancedToggle: {
    en: 'Advanced: use your own Azure app registration',
    he: 'מתקדם: שימוש ברישום יישום Azure שלכם',
  },

  // Combined Test Connection (unified Microsoft 365 OAuth) — one click, both directions.
  channelFormEmailConnTestTitle: { en: 'Test Connection (inbound + outbound)', he: 'בדיקת חיבור (קבלה + שליחה)' },
  channelFormEmailConnTestHint: {
    en: 'Verifies both directions in one click — useful whenever the mailbox is connected via Microsoft 365.',
    he: 'בודק את שני הכיוונים בלחיצה אחת — שימושי כאשר תיבת הדואר מחוברת דרך Microsoft 365.',
  },
  channelFormEmailConnTestButton: { en: 'Test Connection', he: 'בדוק חיבור' },
  channelFormEmailConnTestImapLabel: { en: 'Inbound (IMAP)', he: 'קבלה (IMAP)' },
  channelFormEmailConnTestSmtpLabel: { en: 'Outbound (SMTP)', he: 'שליחה (SMTP)' },
  channelFormImapNeedsReconnect: {
    en: 'The Microsoft connection expired or was revoked - reconnect to resume receiving mail.',
    he: 'החיבור ל-Microsoft פג או בוטל - התחבר מחדש כדי להמשיך לקבל דואר.',
  },
  channelFormImapConnectUnavailable: {
    en: 'Microsoft sign-in is not set up on this deployment yet. An administrator must configure it first.',
    he: 'התחברות Microsoft אינה מוגדרת בהתקנה זו. מנהל מערכת צריך להגדיר אותה קודם.',
  },
  channelFormImapConnectFailed: { en: 'Could not start Microsoft sign-in.', he: 'לא ניתן להתחיל התחברות ל-Microsoft.' },
  channelFormLoading: { en: 'Loading channel...', he: 'טוען ערוץ...' },
  channelFormLoadError: { en: 'Could not load this channel.', he: 'לא ניתן לטעון את הערוץ.' },
  channelFormBackToList: { en: 'Back to channels', he: 'חזרה לערוצים' },
  channelListView: { en: 'View', he: 'הצג' },
  channelFormDeferralIntro: {
    en: 'These settings control when a deferred email answer is treated as overdue or dispatched early during low load. Leave blank to keep the system default.',
    he: 'הגדרות אלה קובעות מתי תשובת אימייל נדחית נחשבת באיחור או נשלחת מוקדם בעומס נמוך. השאר ריק כדי לשמור על ברירת המחדל של המערכת.',
  },
  channelFormDeferralDeadlineLabel: { en: 'Answer deadline (hours)', he: 'מועד אחרון למענה (שעות)' },
  channelFormDeferralDeadlineHint: {
    en: 'Hours before a deferred email answer is considered overdue. System default applies when blank.',
    he: 'מספר השעות לפני שתשובת אימייל נדחית נחשבת באיחור. ברירת המחדל של המערכת חלה כאשר ריק.',
  },
  channelFormDeferralLowLoadLabel: { en: 'Low-load threshold', he: 'סף עומס נמוך' },
  channelFormDeferralLowLoadHint: {
    en: 'Queue size at/below which deferred answers may be dispatched early. System default applies when blank.',
    he: 'גודל תור שבו או מתחתיו ניתן לשלוח תשובות דחויות מוקדם. ברירת המחדל של המערכת חלה כאשר ריק.',
  },
  channelFormDeferralSave: { en: 'Save Deferral Settings', he: 'שמור הגדרות דחייה' },
  channelFormDeferralSaving: { en: 'Saving...', he: 'שומר...' },
  channelFormDeferralSaved: { en: 'Saved.', he: 'נשמר.' },
  channelFormDeferralError: { en: 'Could not save deferral settings. Please try again.', he: 'לא ניתן לשמור את הגדרות הדחייה. נסה שוב.' },
  channelFormDeferralInvalid: { en: 'Enter a non-negative whole number.', he: 'הזן מספר שלם לא שלילי.' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Widget-embed credential rotation (channel list snippet modal). The
  //  ADR-022 embed-security TIER system these once served (L1/L2/L3
  //  selection, per-tier "how to use" guide) was retired 2026-09-08; the
  //  artifacts themselves (Widget Identifier / Secret Key / Secret Value)
  //  stay rotatable — WidgetTokenMinter and WidgetPassportValidator still
  //  depend on them.
  // ═══════════════════════════════════════════════════════════════════════
  channelFormEmbedRelayIntro: {
    en: 'Two script tags, no credential in the page. Your server relays with the API key below — see the integration guide.',
    he: 'שני תגי script, ללא פרטי הרשאה בדף. השרת שלכם מעביר בקשות באמצעות מפתח ה-API למטה — ראו את מדריך האינטגרציה.',
  },
  channelFormEmbedLegacyToggle: {
    en: 'Show the older embed shape (already-deployed pages that have not migrated)',
    he: 'הצג את צורת ההטמעה הישנה (דפים שכבר הוטמעו ולא עברו הסבה)',
  },
  channelFormEmbedGuideLink: {
    en: '📖 Step-by-step installation guide (send this link to the customer)',
    he: '📖 מדריך התקנה שלב אחר שלב (שלחו קישור זה ללקוח)',
  },
  channelEditIntegrationLink: {
    en: 'Integration details, keys and embed code →',
    he: 'פרטי אינטגרציה, מפתחות וקוד הטמעה ←',
  },
  channelListSecretKeyIdLabel: { en: 'Secret key ID (skid):', he: 'מזהה מפתח סוד (skid):' },
  channelListRotateSectionTitle: { en: 'Widget embed credentials', he: 'פרטי הטמעת הווידג׳ט' },
  channelListRotateIdentifier: { en: 'Rotate Identifier', he: 'החלף מזהה' },
  channelListRotateSecretKey: { en: 'Rotate Secret Key', he: 'החלף מפתח סוד' },
  channelListRotateSecretValue: { en: 'Rotate Secret Value', he: 'החלף ערך סוד' },
  channelListRotating: { en: 'Rotating...', he: 'מחליף...' },
  channelListRotateConfirmIdentifier: {
    en: 'Rotate the Widget Identifier? Existing embeds using the old identifier will stop resolving after the grace window.',
    he: 'להחליף את מזהה הווידג׳ט? הטמעות קיימות שמשתמשות במזהה הישן יפסיקו להיפתר לאחר חלון החסד.',
  },
  channelListRotateConfirmSecretKey: {
    en: 'Rotate the Secret Key? The new key is shown once — store it server-side immediately. Old sealed envelopes stop verifying after the grace window.',
    he: 'להחליף את מפתח הסוד? המפתח החדש יוצג פעם אחת — יש לאחסן אותו בצד השרת מיידית. מעטפות חתומות ישנות יפסיקו להיות מאומתות לאחר חלון החסד.',
  },
  channelListRotateConfirmSecretValue: {
    en: 'Rotate the Secret Value (approved domain list hash)? Recompute it from the current Allowed Origins.',
    he: 'להחליף את ערך הסוד (גיבוב רשימת הדומיינים המאושרים)? יחושב מחדש מהמקורות המורשים הנוכחיים.',
  },
  channelListRotateFailed: { en: 'Failed to rotate credentials.', he: 'החלפת הפרטים נכשלה.' },
  channelListNewSecretKeyWarning: {
    en: 'Widget Secret Key — store server-side now — it will NOT be shown again',
    he: 'מפתח סוד הווידג׳ט — יש לאחסן בצד השרת עכשיו — הוא לא יוצג שוב',
  },
  channelListCopyNewSecretKey: { en: 'Copy New Secret Key', he: 'העתק מפתח סוד חדש' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Settings › Chat Widget tab (Problem 3, operator report 2026-08-09) —
  //  discoverable rotation surface for a tenant's WebChat channel(s), listed
  //  here rather than a single tenant-level field: credentials belong to a
  //  ChannelConfig, and a tenant may have more than one WebChat channel.
  // ═══════════════════════════════════════════════════════════════════════
  settingsTabChatWidget: { en: 'Chat Widget', he: 'ווידג׳ט הצ׳אט' },
  chatWidgetSettingsTitle: { en: 'Chat Widget embed credentials', he: 'פרטי הטמעת ווידג׳ט הצ׳אט' },
  chatWidgetSettingsDescription: {
    en: 'Every WebChat channel on this tenant, with its widget credentials. Rotate a credential here, or open a channel for its full embed snippet.',
    he: 'כל ערוצי הצ׳אט (WebChat) של הארגון, עם פרטי ההטמעה של הווידג׳ט. ניתן להחליף פרטים כאן, או לפתוח ערוץ לצפייה בקוד ההטמעה המלא.',
  },
  chatWidgetSettingsLoading: { en: 'Loading chat widget channels...', he: 'טוען ערוצי ווידג׳ט צ׳אט...' },
  chatWidgetSettingsEmpty: {
    en: 'No WebChat channels yet.',
    he: 'אין עדיין ערוצי צ׳אט (WebChat).',
  },
  chatWidgetSettingsEmptyCreateLink: { en: 'Create a WebChat channel', he: 'צור ערוץ צ׳אט (WebChat)' },
  chatWidgetSettingsViewChannelLink: { en: 'Open channel →', he: 'פתח ערוץ ←' },

  // ── ADR-022 embed credentials panel + C# download (2026-08-18) ──────────────
  // The keys an integrator needs live here permanently, so closing the create dialog by accident
  // is no longer the difference between a working integration and a rebuilt channel.
  widgetKeysTitle: { en: 'Embed keys & integration code', he: 'מפתחות הטמעה וקוד אינטגרציה' },
  widgetKeysIntro: {
    en: 'Everything your developer needs to embed the widget. All values below are safe to share with them — the only true secret is the Secret Key, which is shown once when it is created or rotated.',
    he: 'כל מה שהמפתח שלכם צריך כדי להטמיע את הווידג׳ט. כל הערכים למטה ניתנים לשיתוף — הסוד היחיד הוא מפתח הסתר, שמוצג פעם אחת בלבד בעת יצירתו או החלפתו.',
  },
  widgetKeysTenantName: { en: 'Tenant name', he: 'שם הלקוח' },
  widgetKeysTenantNameUnavailable: {
    en: 'Could not load — reload the page before downloading the integration file.',
    he: 'לא ניתן לטעון — רעננו את הדף לפני הורדת קובץ האינטגרציה.',
  },
  widgetKeysSkid: { en: 'Secret Key id (skid)', he: 'מזהה מפתח הסתר (skid)' },
  widgetKeysIdentifier: { en: 'Widget Identifier', he: 'מזהה הווידג׳ט' },
  widgetKeysDomains: { en: 'Approved domains (hosts only)', he: 'דומיינים מאושרים (שמות מארח בלבד)' },
  widgetKeysDomainsEmpty: {
    en: 'None set — add at least one approved origin to this channel or every handshake will be rejected.',
    he: 'לא הוגדרו — הוסיפו לפחות מקור מורשה אחד לערוץ, אחרת כל לחיצת יד תידחה.',
  },
  widgetKeysSessionLifetime: { en: 'Session lifetime (minutes)', he: 'משך חיי הסשן (דקות)' },
  widgetKeysSessionLifetimeHint: {
    en: 'How long a visitor stays signed in to chat before the widget renews. Change it on this channel’s Edit page.',
    he: 'משך הזמן שבו מבקר נשאר מחובר לצ׳אט לפני חידוש. ניתן לשנות בעמוד העריכה של הערוץ.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  The environment half of an integration — served by the API, never
  //  derived from this bundle's build-time VITE_* variables. Those are empty
  //  in the shipped images, which is how the snippet a tenant copied came to
  //  read "https://YOUR_API_HOST/...".
  // ═══════════════════════════════════════════════════════════════════════
  widgetKeysConnectTitle: { en: 'Connect your site', he: 'חיבור האתר שלכם' },
  widgetKeysConnectIntro: {
    en: 'Everything your developer needs, and nothing they do not. Your server holds one secret — the API key below. The page itself carries no credential, no tenant id and no widget id, which is what makes it safe to cache.',
    he: 'כל מה שהמפתח שלכם צריך, ולא יותר. השרת שלכם מחזיק סוד אחד בלבד — מפתח ה-API שלמטה. הדף עצמו אינו נושא שום אסימון.',
  },
  widgetKeysApiHost: { en: 'BotableX API host', he: 'כתובת ה-API של BotableX' },
  widgetKeysBootstrapUrl: { en: 'Widget script URL (second script tag)', he: 'כתובת סקריפט הווידג׳ט (תג שני)' },
  widgetKeysMintUrl: { en: 'Your server relays to (new chat)', he: 'השרת שלכם מעביר אל (צ׳אט חדש)' },
  widgetKeysRefreshUrl: { en: 'Your server relays to (renewal)', he: 'השרת שלכם מעביר אל (חידוש)' },
  widgetKeysEnvLoading: { en: 'Loading your integration details…', he: 'טוען את פרטי האינטגרציה…' },
  widgetKeysEnvUnavailable: {
    en: 'Could not load your integration details. Reload the page — do not copy a snippet until these appear, because the addresses your developer needs are exactly what is missing.',
    he: 'לא ניתן לטעון את פרטי האינטגרציה. רעננו את הדף ואל תעתיקו קטע קוד עד שיופיעו.',
  },
  widgetKeysWidgetDisabled: {
    en: 'The chat widget is currently switched off for this deployment. A correct integration will load but will not open a chat, and nothing in the browser will explain why. Ask your BotableX administrator to enable it before testing.',
    he: 'וידג׳ט הצ׳אט כבוי כרגע בסביבה זו. אינטגרציה תקינה תיטען אך לא תפתח צ׳אט.',
  },
  widgetKeysDomainsEditHint: {
    en: 'Only these sites may open a chat with this channel. Add or remove them on the channel’s Edit page.',
    he: 'רק אתרים אלו רשאים לפתוח צ׳אט בערוץ זה. ניתן לערוך בעמוד העריכה של הערוץ.',
  },
  widgetKeysEditChannelLink: { en: 'Edit channel settings →', he: 'עריכת הגדרות הערוץ ←' },
  widgetKeysDomainsSave: { en: 'Save', he: 'שמור' },
  widgetKeysDomainsSaving: { en: 'Saving…', he: 'שומר…' },
  widgetKeysDomainsSaved: { en: 'Saved.', he: 'נשמר.' },
  widgetKeysDomainsSaveFailed: {
    en: 'Could not save the approved domains.',
    he: 'לא ניתן היה לשמור את הדומיינים המאושרים.',
  },
  widgetKeysDomainsHint: {
    en: 'Comma-separated, full origins including https:// and the port if you use one. Only these sites may open a chat on this channel.',
    he: 'מופרדים בפסיקים, מקור מלא כולל https:// והפורט אם יש. רק אתרים אלו רשאים לפתוח צ׳אט בערוץ זה.',
  },
  widgetKeysDomainsInvalid: {
    en: '“{entry}” is not a valid site origin. Use the full origin, for example https://www.example.com — no wildcards, no paths.',
    he: '“{entry}” אינו מקור חוקי. הזינו מקור מלא, לדוגמה https://www.example.com.',
  },
  widgetKeysDomainsTooLong: {
    en: 'One of the entries is too long to be a site origin.',
    he: 'אחת הרשומות ארוכה מדי.',
  },
  widgetKeysDomainsTooMany: {
    en: 'Too many approved domains — the maximum is 25.',
    he: 'יותר מדי דומיינים מאושרים — המקסימום הוא 25.',
  },

  // ── The older Passport generation (ADR-023), kept and demoted ──────────
  widgetKeysAdvancedToggle: {
    en: 'Advanced — Passport integration (older method)',
    he: 'מתקדם — אינטגרציית Passport (שיטה ישנה)',
  },
  widgetKeysAdvancedIntro: {
    en: 'An earlier integration method, still supported. Your server seals its own credential with the channel Secret Key instead of relaying an API key. Use it only if you are already on it — new sites should use the method above, which needs no cryptography on your side.',
    he: 'שיטת אינטגרציה קודמת שעדיין נתמכת. אתרים חדשים אמורים להשתמש בשיטה שלמעלה.',
  },
  widgetKeysPassportDormant: {
    en: 'Passport mode is switched off for this deployment, so these values are not currently accepted by the server.',
    he: 'מצב Passport כבוי בסביבה זו, ולכן השרת אינו מקבל ערכים אלו כרגע.',
  },
  widgetKeysSecretKey: { en: 'Secret Key', he: 'מפתח סתר' },
  widgetKeysSecretKeySet: {
    en: 'Set — hidden. It cannot be shown again; rotate to get a new one. Rotating also issues a NEW Secret Key id, so download the integration file again afterwards.',
    he: 'מוגדר — מוסתר. לא ניתן להציגו שוב; החליפו את מפתח הסתר כדי לקבל חדש.',
  },
  widgetKeysSecretKeyMissing: {
    en: 'Not provisioned yet.',
    he: 'עדיין לא הוגדר.',
  },
  // ═══════════════════════════════════════════════════════════════════════
  //  ADR-029 — tenant API key (mint / rotate / revoke)
  // ═══════════════════════════════════════════════════════════════════════
  widgetKeysApiKeySection: { en: 'Tenant API key', he: 'מפתח API של הלקוח' },
  widgetKeysApiKeyId: { en: 'API key id', he: 'מזהה מפתח API' },
  widgetKeysApiKeyIntro: {
    en: 'Your server sends this key to mint a chat ticket for each page render. It is the only BotableX secret you hold — treat it like a database password.',
    he: 'השרת שלכם שולח את המפתח הזה כדי להנפיק כרטיס צ׳אט בכל טעינת עמוד. זהו הסוד היחיד של BotableX שברשותכם — התייחסו אליו כמו לסיסמת מסד נתונים.',
  },
  widgetKeysApiKeySet: {
    en: 'Set — hidden. It cannot be shown again; rotate to get a new one.',
    he: 'מוגדר — מוסתר. לא ניתן להציגו שוב; בצעו החלפה כדי לקבל חדש.',
  },
  widgetKeysApiKeyMissing: { en: 'Not issued yet.', he: 'עדיין לא הונפק.' },
  widgetKeysApiKeyIssue: { en: 'Issue API key', he: 'הנפקת מפתח API' },
  widgetKeysApiKeyRotate: { en: 'Rotate (24h grace)', he: 'החלפה (חסד 24 שעות)' },
  widgetKeysApiKeyRevoke: { en: 'Revoke immediately', he: 'ביטול מיידי' },
  widgetKeysApiKeyWorking: { en: 'Working...', he: 'מבצע...' },
  widgetKeysApiKeyRotateConfirm: {
    en: 'Rotate the API key? The new key is shown once — store it server-side immediately. The current key keeps working for 24 hours so you can cut over without an outage. Note: only ONE old key is remembered, so rotating twice within that window ends the first one early.',
    he: 'להחליף את מפתח ה-API? המפתח החדש יוצג פעם אחת — יש לאחסנו בצד השרת מיידית. המפתח הנוכחי ימשיך לעבוד 24 שעות כדי לאפשר מעבר ללא השבתה. לתשומת לבכם: נשמר רק מפתח ישן אחד, ולכן שתי החלפות בתוך החלון יסיימו את הראשון מוקדם.',
  },
  widgetKeysApiKeyRevokeConfirm: {
    en: 'Revoke the current API key NOW? Any server still using it stops being able to start chats immediately, with no grace window. A new key is issued and shown once. Use this if the key may have leaked.',
    he: 'לבטל את מפתח ה-API הנוכחי עכשיו? כל שרת שעדיין משתמש בו יאבד מיד את היכולת לפתוח צ׳אטים, ללא חלון חסד. יונפק מפתח חדש שיוצג פעם אחת. השתמשו בכך אם ייתכן שהמפתח נחשף.',
  },
  widgetKeysApiKeyRevokeConfirmWord: { en: 'REVOKE', he: 'REVOKE' },
  widgetKeysApiKeyRevealTitle: {
    en: 'API key — store it server-side now — it will NOT be shown again',
    he: 'מפתח API — אחסנו בצד השרת עכשיו — הוא לא יוצג שוב',
  },
  widgetKeysApiKeyFailed: { en: 'Could not change the API key.', he: 'שינוי מפתח ה-API נכשל.' },
  widgetKeysApiKeyNeedsSecretKey: {
    en: 'Provision the widget credentials first — an API key cannot mint a chat token without a Secret Key on this channel.',
    he: 'הגדירו קודם את פרטי הווידג׳ט — מפתח API אינו יכול להנפיק אסימון צ׳אט ללא מפתח סתר בערוץ זה.',
  },
  widgetKeysEmbedSnippetTitle: { en: 'Embed snippet', he: 'קטע ההטמעה' },
  widgetKeysEmbedSnippetHint: {
    en: 'Your server mints a ticket per page render and renders it with the loader below. The page carries no key and no visitor identity.',
    he: 'השרת שלכם מנפיק כרטיס בכל טעינת עמוד ומשלב אותו עם הטוען שלהלן. העמוד אינו נושא מפתח או זהות מבקר.',
  },
  widgetKeysCopy: { en: 'Copy', he: 'העתק' },
  widgetKeysCopied: { en: 'Copied!', he: 'הועתק!' },
  widgetKeysDownloadCSharp: { en: 'Download C# integration file', he: 'הורדת קובץ אינטגרציה ‏C#' },
  widgetKeysDownloadCSharpHint: {
    en: 'A single self-contained .cs file with your channel’s values already filled in. Drop it into your ASP.NET project, build one widget at startup, then call widget.BuildEmbed(visitor) on each page render and output it inside <body>. Requires .NET 8 or newer. Rotating the Secret Key changes the key id too — download this file again after every rotation.',
    he: 'קובץ .cs עצמאי אחד שכבר מכיל את ערכי הערוץ שלכם. הוסיפו אותו לפרויקט ASP.NET, צרו ווידג׳ט אחד בעלייה, וקראו ל‏-widget.BuildEmbed(visitor) בכל טעינת דף — הפלט נכנס בתוך ‏<body>. דורש ‏.NET 8 ומעלה. החלפת מפתח הסתר משנה גם את מזהה המפתח — הורידו את הקובץ מחדש אחרי כל החלפה.',
  },
  widgetKeysDownloadBlocked: {
    en: 'Provision this channel’s embed credentials first — the generated file would not work without them.',
    he: 'הגדירו תחילה את פרטי ההטמעה של הערוץ — בלעדיהם הקובץ שייווצר לא יעבוד.',
  },
  widgetKeysNodeExampleToggle: { en: 'Prefer Node.js? Show the server-side example', he: 'מעדיפים ‏Node.js? הצג דוגמת שרת' },

  widgetKeysLoaderUrlEditable: { en: 'Loader URL (check before downloading)', he: 'כתובת הטוען (בדקו לפני ההורדה)' },
  widgetKeysLoaderUrlLocalWarning: {
    en: 'This is a development address and will not work on the customer’s site — it is what this admin portal build was compiled with. Replace it with your production widget host (for example https://chat.botablex.io) before downloading.',
    he: 'זו כתובת פיתוח ולא תעבוד באתר הלקוח — זו הכתובת שאיתה נבנתה גרסת הפורטל הזו. החליפו אותה בכתובת הייצור שלכם (למשל https://chat.botablex.io) לפני ההורדה.',
  },
} satisfies Record<string, { en: string; he: string }>;
